import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ViewState, Case, RiskLevel, Category, SubmissionData, AIAnalysisResult, PendingInvite } from './types';
import { ManagerLayout, WorkerLayout } from './components/Layouts';
import { OperatorUsageView } from './views/OperatorUsageView';
import { WorkerHome, WorkerInput, WorkerConfirm, WorkerSuccess, clearReportDraft } from './views/WorkerFlow';
import { ManagerCasesView, ManagerQRView, ManagerSettingsView } from './views/ManagerViews';
import { MonthlyReportView } from './views/MonthlyReportView';
import { IndustryLandingView } from './views/IndustryLandingView';
import { GuideView, LegalView, PricingView } from './views/PublicViews';
import { AboutView } from './views/AboutView';
import { WhyAnalysisDemo } from './views/WhyAnalysisDemo';
import { DemoExperience } from './views/DemoExperience';
import { ManagerImportView } from './views/ManagerImportView';
import { ProfileSection } from './components/AuthorBox';
import { MyReportView } from './views/MyReportView';
import { QrInvalidView, QrInvalidReason } from './views/QrInvalidView';
import { MyReportsListView } from './views/MyReportsListView';
import { PosterGalleryView } from './views/PosterGalleryView';
import { LoginView } from './components/AuthComponents';
import { StatCard, ActivityChart, RiskPieChart, RecentCasesList, HeroDashboardMockup, UsageStatsCard } from './components/DashboardComponents';
import { DeckDownloadCTA } from './components/DeckDownloadCTA';
import { LandingDemoGuide, LandingPosters, LandingClosingCTA, LandingChangelog } from './components/LandingSections';
import { GenbaChatWidget } from './components/GenbaChatWidget';
import {
  trackSignupStarted,
  trackSignupCompleted,
  trackCaseSubmitted,
  trackCaseResolved,
  trackSubscriptionStarted,
} from './utils/analytics';
import { filterRealCases } from './utils/caseClassification';
import { pathToView, viewToPath, isPublicPath, requiresAuth } from './routes';
import { useAppStore } from './store/useAppStore';
import { logger } from './utils/logger';
import {
  getCases,
  getQRPoints,
  getTeams,
  isSupabaseConfigured,
  getOrganizationByUserId,
  getCurrentUser,
  getCurrentUserContext,
  mapUserProfile,
  createQRPoint,
  updateQRPoint,
  deleteQRPoint,
  getSupabase,
  provisionUser,
  getQRPointByToken,
  incrementQRPointScans,
  createCase,
  getOrganizationMonthlyStats,
  getUsageStats,
  getLastDbError,
  updateCaseStatus,
  updateCaseLesson,
  uploadCaseImage,
  getLastImageUploadError,
  UsageStats,
  checkIsOperator,
} from './services/supabase';
import { logInfo, logError, logWarn, formatLogsForDisplay } from './services/clientLogger';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Shield,
  Zap,
  Lock,
  ChevronRight,
  QrCode,
  BarChart3,
  Menu,
  X,
  Loader2,
  ExternalLink,
  Copy,
  Smartphone,
  Clock,
  Inbox,
  Percent,
  ArrowUpDown,
  Download,
  Printer,
  Sparkles,
  ArrowRight,
  Monitor
} from 'lucide-react';

function App() {
  const [view, setView] = useState<ViewState>('landing');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  /* 別の組織から招待されているとき、本人に確認を出すための情報。
     合流するとこの人の過去の報告が招待元の管理者から見えるため、
     移すものがある場合は必ず本人に聞く。 */
  const [pendingInvite, setPendingInvite] = useState<PendingInvite | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  /* 合流の失敗はバナーの中に出す。dataError はダッシュボードでしか
     描画されないため、ケース一覧や設定画面から押すと無言で終わっていた。 */
  const [inviteError, setInviteError] = useState<string | null>(null);
  /* 招待の確認は1セッションに1回でよい。ダッシュボードを開くたびに
     Edge Function を叩くとレイテンシとコストが増える。 */
  const inviteCheckedRef = useRef(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [currentQRToken, setCurrentQRToken] = useState<string | null>(null);
  const [isLoadingQR, setIsLoadingQR] = useState(false);
  const [workerStats, setWorkerStats] = useState<{ monthPosts: number; resolvedCount: number }>({ monthPosts: 0, resolvedCount: 0 });
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  // 投稿前に表示する AI 分析の月次利用回数。WorkerInput / WorkerConfirm に props で渡す。
  const [isUsageLoading, setIsUsageLoading] = useState(false);
  const [inAppBrowserWarning, setInAppBrowserWarning] = useState<{ show: boolean; url: string } | null>(null);
  const [urlCopied, setUrlCopied] = useState(false);
  // 既存ケースから「深掘り分析」を呼んだときに WhyAnalysisDemo にプリセット投入する事象テキスト.
  // sourceCaseId が入っているときは「ケース詳細から呼ばれた」状態. 戻る/保存ボタンを出す.
  const [whyDemoPreset, setWhyDemoPreset] = useState<{
    problem: string;
    autoStart: boolean;
    sourceCaseId?: string;
  } | null>(null);
  // 直近に投稿が成功した case の情報。WorkerSuccess に viewer_token を渡すために保持。
  const [lastSubmittedCase, setLastSubmittedCase] = useState<{
    caseId: string;
    title: string;
    body?: string;
    viewerToken?: string;
    /** 写真を添えたのにアップロードに失敗した場合 true。完了画面で作業者に明示する。 */
    imageUploadFailed?: boolean;
  } | null>(null);
  // /my-report/{token} 直リンクで開かれたときの token。view==='my_report' のときに使う。
  const [myReportToken, setMyReportToken] = useState<string | null>(null);
  /** QRが使えなかった理由。掲示物は回収できないので専用画面で作業者に説明する */
  const [qrInvalid, setQrInvalid] = useState<{ reason: QrInvalidReason; token: string } | null>(null);
  // Dashboard Hero から「フィルタ済みのケース一覧へ遷移」した時に、その意図を ManagerCasesView の初期値として渡す。
  // navigation 後は次回のフィルタ干渉を避けるためにクリアする。
  const [casesNavIntent, setCasesNavIntent] = useState<{ risk?: RiskLevel | 'all'; status?: Case['status'] | 'all' } | null>(null);

  // Zustand store
  const {
    cases,
    setCases,
    addCase,
    updateCase,
    organization,
    setOrganization,
    qrPoints,
    setQRPoints,
    teams,
    setTeams,
    isAuthenticated,
    setAuthenticatedUser,
    logout,
    addToast,
    setQRContext,
    qrContext
  } = useAppStore();

  // Detect in-app browsers (LINE, Instagram, Facebook, Twitter, etc.)
  const detectInAppBrowser = useCallback((): { isInApp: boolean; browserName: string } => {
    const ua = navigator.userAgent || navigator.vendor || '';

    // LINE in-app browser
    if (/Line/i.test(ua)) {
      return { isInApp: true, browserName: 'LINE' };
    }
    // Instagram in-app browser
    if (/Instagram/i.test(ua)) {
      return { isInApp: true, browserName: 'Instagram' };
    }
    // Facebook in-app browser (FBAN or FBAV)
    if (/FBAN|FBAV/i.test(ua)) {
      return { isInApp: true, browserName: 'Facebook' };
    }
    // Twitter in-app browser
    if (/Twitter/i.test(ua)) {
      return { isInApp: true, browserName: 'Twitter' };
    }
    // TikTok in-app browser
    if (/TikTok/i.test(ua)) {
      return { isInApp: true, browserName: 'TikTok' };
    }
    // WeChat in-app browser
    if (/MicroMessenger/i.test(ua)) {
      return { isInApp: true, browserName: 'WeChat' };
    }
    // Generic WebView detection (Android)
    if (/wv|WebView/i.test(ua) && /Android/i.test(ua)) {
      return { isInApp: true, browserName: 'アプリ内ブラウザ' };
    }

    return { isInApp: false, browserName: '' };
  }, []);

  // Copy URL to clipboard
  const copyUrlToClipboard = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    }
  }, []);

  // Hero から渡したフィルタ意図は、cases 画面を離れた時点でクリアする。
  // クリアしないと、その後サイドバーから cases を開き直しても prev intent で絞り込まれてしまう。
  useEffect(() => {
    if (view !== 'manager_cases' && casesNavIntent) {
      setCasesNavIntent(null);
    }
  }, [view, casesNavIntent]);

  // 深掘り分析の preset 残留対策: why_demo を離れたらクリア
  useEffect(() => {
    if (view !== 'why_demo' && whyDemoPreset) {
      setWhyDemoPreset(null);
    }
  }, [view, whyDemoPreset]);

  /* QR管理から掲示ポスターへ渡す「最初に選んでおくQRポイント」。
     現場ごとにQRを分けている運用で、別の現場のQRを刷ってしまう事故を防ぐ。
     ポスター画面を離れたらクリアする（次に自分で開いたとき選択が残らないように）。 */
  const [posterQrPointId, setPosterQrPointId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (view !== 'posters' && posterQrPointId) setPosterQrPointId(undefined);
  }, [view, posterQrPointId]);

  // Navigation with browser history support
  // URL ↔ view の対応は routes.ts が唯一の定義（以前は3箇所に重複していた）。
  /* いまの画面名を window に出す。**エラー記録のためだけ**に使う。
     管理画面は遷移しても URL が変わらないので、path だけでは
     ダッシュボードで起きたエラーも `/login` と記録されてしまう。 */
  useEffect(() => {
    (window as unknown as { __anzenView?: string }).__anzenView = view;
  }, [view]);

  const navigateTo = useCallback((newView: ViewState, replace = false) => {
    const path = viewToPath(newView);

    if (replace) {
      window.history.replaceState({ view: newView }, '', path);
    } else {
      window.history.pushState({ view: newView }, '', path);
    }

    setView(newView);
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state?.view) {
        setView(event.state.view);
        return;
      }
      // state が null のケース（ハッシュナビゲーション含む）。
      // pathname から view を導出して、ハッシュリンククリックで view が
      // 強制的に 'landing' に戻ってしまわないようにする。
      setView(pathToView(window.location.pathname));
    };

    window.addEventListener('popstate', handlePopState);

    // Set initial state - but skip if there's an auth callback hash
    const currentPath = window.location.pathname;
    const currentHash = window.location.hash;
    const hasAuthParams = currentHash && (currentHash.includes('access_token') || currentHash.includes('error') || currentHash.includes('token_type'));

    // Don't modify URL if it's an auth callback - let the auth handler deal with it
    if (!hasAuthParams) {
      const initialView = pathToView(currentPath);

      // Only set initial state if not a special route (like /report/TOKEN or /my-report/{uuid})
      if (!currentPath.startsWith('/report/') && !currentPath.startsWith('/my-report/')) {
        window.history.replaceState({ view: initialView }, '', currentPath);
        // 既存バグ修正: URL に対応する view が 'landing' 以外（/guide /pricing /legal /login /lp/...）
        // の場合、useState の初期値 'landing' から正しい view に切り替える。これがないと、
        // 静的HTMLは正しい title を返すのにレンダリング内容は常に landing になっていた。
        if (initialView !== 'landing') {
          setView(initialView);
        }
      }
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // URL routing - handle /report/TOKEN paths for QR code scanning
  useEffect(() => {
    const handleURLRouting = async () => {
      const pathname = window.location.pathname;
      logger.log('URL routing check, pathname:', pathname);

      // /my-report/{viewer_token} : 投稿者本人の状況確認画面
      // ヒヤリ報告者は匿名なので、URL に埋め込まれた token のみで認証する。
      const myReportMatch = pathname.match(/^\/my-report\/([0-9a-f-]{36})\/?$/i);
      if (myReportMatch) {
        const token = myReportMatch[1];
        logger.log('MyReport token detected from URL:', token);
        setMyReportToken(token);
        setView('my_report');
        return;
      }

      // Check for /report/TOKEN pattern
      const reportMatch = pathname.match(/^\/report\/([a-zA-Z0-9]+)$/);
      if (reportMatch) {
        const token = reportMatch[1];
        const userAgent = navigator.userAgent;
        logger.log('QR token detected from URL:', token);
        logger.log('User agent:', userAgent);

        // Store token in sessionStorage for recovery
        try {
          sessionStorage.setItem('genbasafeai_qr_token', token);
          logger.log('QR token saved to sessionStorage');
        } catch (e) {
          logger.warn('Failed to save token to sessionStorage:', e);
        }

        // Check for in-app browser
        const { isInApp, browserName } = detectInAppBrowser();
        logger.log('In-app browser check:', { isInApp, browserName });

        if (isInApp) {
          logger.warn('In-app browser detected:', browserName);
          const fullUrl = window.location.href;
          setInAppBrowserWarning({ show: true, url: fullUrl });
          return; // Don't proceed with QR loading in in-app browser
        }

        setIsLoadingQR(true);
        setCurrentQRToken(token);

        try {
          // Fetch QR point data
          logger.log('Fetching QR point for token:', token);
          const qrPoint = await getQRPointByToken(token);
          logger.log('QR point fetched:', qrPoint ? {
            id: qrPoint.id,
            name: qrPoint.name,
            organizationId: qrPoint.organizationId,
            isActive: qrPoint.isActive
          } : 'null');

          if (qrPoint && qrPoint.isActive) {
            // Set QR context for worker flow
            const newQrContext = {
              token,
              qrPointId: qrPoint.id,
              qrPointName: qrPoint.name,
              teamName: qrPoint.name,
              organizationId: qrPoint.organizationId,
              teamId: qrPoint.teamId || '',
              equipmentInfo: qrPoint.equipmentInfo // 設備情報（AIコンテキスト用）
            };
            logger.log('Setting QR context:', newQrContext);
            setQRContext(newQrContext);

            // Also store context in sessionStorage for recovery
            try {
              sessionStorage.setItem('genbasafeai_qr_context', JSON.stringify(newQrContext));
              logger.log('QR context saved to sessionStorage');
            } catch (e) {
              logger.warn('Failed to save context to sessionStorage:', e);
            }

            // Increment scan count (don't await to speed up loading)
            incrementQRPointScans(token).then(() => {
              logger.log('QR scan count incremented');
            }).catch(err => {
              logger.warn('Failed to increment scan count:', err);
            });

            // Fetch monthly stats for this organization
            logger.log('Fetching stats for organization:', qrPoint.organizationId);
            try {
              const stats = await getOrganizationMonthlyStats(qrPoint.organizationId);
              logger.log('Worker stats loaded:', stats, 'for org:', qrPoint.organizationId);
              setWorkerStats(stats);
            } catch (statsError) {
              logger.error('Failed to load stats:', statsError);
              // Use default stats but continue
              setWorkerStats({ monthPosts: 0, resolvedCount: 0 });
            }

            // Navigate to worker flow
            setView('worker_landing');
          } else {
            // 掲示済みのポスターは回収できないため、LPに落とさず理由を説明する
            logger.error('QR point not found or inactive:', token, qrPoint);
            setQrInvalid({ reason: 'inactive', token });
            setView('qr_invalid');
          }
        } catch (error) {
          // 通信断は画像やトークンの問題ではないので、再試行を促す
          logger.error('Error loading QR point:', error);
          setQrInvalid({ reason: 'network', token });
          setView('qr_invalid');
        } finally {
          setIsLoadingQR(false);
        }

        // Clear the URL to prevent re-processing
        window.history.replaceState(null, '', '/');
        return;
      }

      // Check sessionStorage for stored token (recovery case)
      try {
        const storedToken = sessionStorage.getItem('genbasafeai_qr_token');
        const storedContext = sessionStorage.getItem('genbasafeai_qr_context');

        if (storedToken && storedContext && !qrContext?.organizationId) {
          logger.log('Recovering QR context from sessionStorage');
          const parsedContext = JSON.parse(storedContext);
          setCurrentQRToken(storedToken);
          setQRContext(parsedContext);

          // Fetch fresh stats
          if (parsedContext.organizationId) {
            getOrganizationMonthlyStats(parsedContext.organizationId).then(stats => {
              setWorkerStats(stats);
            }).catch(err => {
              logger.warn('Failed to refresh stats:', err);
            });
          }
        }
      } catch (e) {
        logger.warn('Failed to recover from sessionStorage:', e);
      }
    };

    handleURLRouting();
  }, []);

  /**
   * Supabase のセッションからストアの user / isAuthenticated を復元する。
   *
   * isAuthenticated の唯一の供給元。以前はデモモードのログイン処理からしか setUser を
   * 呼んでおらず、本番のマジックリンク認証でもセッション復元でも真にならなかったため、
   * ログイン済み前提の機能（掲示ポスターの実トークンQR、公開ヘッダーの出し分け）が
   * 一度も動いていなかった。
   *
   * プロフィール未作成（provision 前）の場合は何もしない。この経路のユーザーは
   * 必ず manager 画面に着地し、loadDashboardData の provisionUser 後に改めて set される。
   */
  /* 運営者判定を取り直す。ログイン確定時とサインアウト時に呼ぶ。 */
  const refreshOperatorFlag = useCallback(async (signedIn: boolean) => {
    if (!signedIn) {
      setIsOperator(false);
      return;
    }
    setIsOperator(await checkIsOperator());
  }, []);

  const syncAuthenticatedUser = useCallback(async () => {
    const { user, organization: org } = await getCurrentUserContext();
    if (!user) {
      logger.log('Session exists but no app user profile yet (awaiting provisioning)');
      return;
    }
    // organizations の取得に失敗したときに、既に持っている組織を消さない。
    setAuthenticatedUser(user, org ?? useAppStore.getState().organization);
    // 運営者判定はサイドバーの出し分けのみに使う（データ側の認可は RPC 内）。
    void refreshOperatorFlag(true);
  }, [setAuthenticatedUser, refreshOperatorFlag]);

  // Handle MagicLink auth callback and session check
  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase) return;

    let isMounted = true;

    // Listen for auth state changes - this handles MagicLink tokens automatically
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      logger.log('Auth state change:', event, !!session);

      if (!isMounted) return;

      if (event === 'SIGNED_IN' && session) {
        logger.log('User signed in:', session.user.email);
        // ストアの認証状態を実セッションに合わせる（画面遷移より先に投げる）
        void syncAuthenticatedUser();
        // Clear the hash/auth-query from URL after successful sign in
        // (MagicLink uses #access_token; Google OAuth uses ?code=)
        if (
          window.location.hash ||
          window.location.search.includes('code=') ||
          window.location.search.includes('error=')
        ) {
          window.history.replaceState(null, '', window.location.pathname);
        }
        // Only show toast and redirect if not already on a manager page
        // This prevents unwanted redirects on token refresh or page reload
        setView(currentView => {
          if (!currentView.startsWith('manager_')) {
            addToast({
              type: 'success',
              message: 'ログインしました'
            });
            return 'manager_dashboard';
          }
          return currentView;
        });
        setIsAuthenticating(false);
        setAuthError(null);
        if (authTimeout) clearTimeout(authTimeout);
      } else if (event === 'SIGNED_OUT') {
        // ストアを空にする。ここを抜かすと isAuthenticated が真のまま残り、
        // 公開ページが「管理画面へ」を出し続け、ポスターに前の組織の実QRが残る。
        logout();
        void refreshOperatorFlag(false);
        /* 招待の確認は1セッション1回にしているので、ログアウトで戻す。
           戻さないと、同じタブで別アカウントに入り直したときに招待に
           気づけず、バナーの「次回ログイン時にまたお尋ねします」が嘘になる。 */
        inviteCheckedRef.current = false;
        setPendingInvite(null);
        setInviteError(null);
        setView('landing');
      } else if (event === 'TOKEN_REFRESHED') {
        logger.log('Token refreshed');
      }
    });

    // Auth timeout - if SIGNED_IN doesn't fire within 10 seconds, show error
    let authTimeout: ReturnType<typeof setTimeout> | null = null;

    // Check for existing session on mount
    const checkSession = async () => {
      // Check if this is an auth callback.
      // MagicLink returns tokens in the URL hash (#access_token=...);
      // Google OAuth (PKCE) returns ?code=... / ?error=... in the query string.
      const hash = window.location.hash;
      const queryParams = new URLSearchParams(window.location.search);
      const hasHashAuth = !!hash && (hash.includes('access_token') || hash.includes('error') || hash.includes('token_type'));
      const hasQueryAuth = queryParams.has('code') || queryParams.has('error');
      const hasAuthParams = hasHashAuth || hasQueryAuth;

      if (hasAuthParams) {
        // Check for an error in either hash or query (expired token, consent denied, etc.)
        if (hash.includes('error') || queryParams.has('error')) {
          const errorDesc =
            new URLSearchParams(hash.substring(1)).get('error_description') ||
            queryParams.get('error_description');
          logger.error('Auth callback error:', errorDesc);
          setAuthError(errorDesc || 'ログインに失敗しました。再度ログインしてください。');
          window.history.replaceState(null, '', window.location.pathname);
          return;
        }

        logger.log('Auth callback detected, waiting for onAuthStateChange...');
        setIsAuthenticating(true);

        // Set timeout to prevent infinite loading
        authTimeout = setTimeout(() => {
          if (isMounted) {
            logger.error('Auth callback timed out after 10 seconds');
            setIsAuthenticating(false);
            setAuthError('ログインに失敗しました。リンクの有効期限が切れている可能性があります。再度ログインしてください。');
            window.history.replaceState(null, '', window.location.pathname);
          }
        }, 10000);

        return;
      }

      // No auth params - check for existing session (only on initial mount)
      const { data } = await supabase.auth.getSession();
      if (!isMounted) return;

      const currentPath = window.location.pathname;

      if (data.session) {
        logger.log('Existing session found:', data.session.user.email);
        // リロード後もストアの認証状態を復元する。/posters のように manager 画面を
        // 経由しない公開ルートでは、ここが唯一の復元経路になる。
        void syncAuthenticatedUser();
        // ログイン済みで landing / login に来たときだけダッシュボードへ送る（routes.ts の
        // RouteAccess='public' の定義どおり）。
        //
        // ここは以前クロージャの `view` を見ていたが、この useEffect の deps は [] で、
        // `view` の初期値は常に 'landing'（実 path に対応する view は別の effect が後から
        // setView する）。そのため実際には「セッションがあれば全公開ページからダッシュボードへ
        // 飛ばす」挙動になっていて、ログイン中は /posters /guide /pricing を直接開けなかった。
        // path から view を引き直して本来の意図に戻す。
        const pathView = pathToView(currentPath);
        if (isPublicPath(currentPath) && (pathView === 'login' || pathView === 'landing')) {
          setView('manager_dashboard');
        }
      } else if (requiresAuth(currentPath)) {
        // /manager/* をセッション無しで直接開かれた場合。空の管理画面を見せずログインへ。
        logger.log('Auth required for path, redirecting to login:', currentPath);
        setView('login');
      }
    };

    // Only run session check once on mount
    checkSession();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (authTimeout) clearTimeout(authTimeout);
    };
  }, []);

  /* 招待を受け入れて合流する。移動はサーバー側の1トランザクション
     （genbasafeai.accept_invite_merge）で行う。席不足・QR上限で断られた
     ときは、その理由をそのまま出す——一般文言に丸めると、管理者に
     何を頼めばよいか分からない。 */
  const acceptPendingInvite = useCallback(async () => {
    if (!pendingInvite) return;
    setInviteBusy(true);
    setInviteError(null);
    const result = await provisionUser({ acceptInvite: true, inviteId: pendingInvite.inviteId });
    setInviteBusy(false);
    if (result.error || !result.organization) {
      /* 席不足・QR上限・招待の取消。**理由をそのままバナーに出す。**
         押した画面がどこであっても見えるようにする。 */
      setInviteError(result.error ?? 'ただいま合流できませんでした。');
      return;
    }
    setPendingInvite(null);
    /* 役割は招待の指定で変わる（自分の組織では admin だった人が
       leader になることがある）。組織と一緒に認証状態を差し替えないと、
       画面は admin のまま操作が RLS で黙って失敗する。
       organization を差し替えると loadDashboardData の identity が変わり、
       既存の effect が新しい組織で読み直す。**ここで手動に呼ぶと
       古い closure と二重に走り、遅く返った方が勝つ。** */
    const nextUser = result.user ? mapUserProfile(result.user) : null;
    if (nextUser) setAuthenticatedUser(nextUser, result.organization);
    else setOrganization(result.organization);
  }, [pendingInvite]);

  // Load data from Supabase when entering manager dashboard
  const loadDashboardData = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      logger.log('Supabase not configured, using demo mode');
      return;
    }

    setIsDataLoading(true);
    setDataError(null);

    try {
      // Get current user and organization
      const user = await getCurrentUser();
      if (!user) {
        logger.log('No authenticated user');
        setIsDataLoading(false);
        return;
      }

      let org = organization;
      if (!org) {
        org = await getOrganizationByUserId(user.id);
        if (org) {
          setOrganization(org);
        }
      }

      /* すでに組織がある人にも、別組織からの招待が届いていることがある。
         provisionUser は組織が無いときしか呼ばれないので、ここで1回だけ聞く。 */
      if (org && !inviteCheckedRef.current) {
        inviteCheckedRef.current = true;
        const probe = await provisionUser();
        if (probe.pendingInvite) setPendingInvite(probe.pendingInvite);
        /* **移すものが無い人は、この呼び出しの中でサーバーが合流を済ませる。**
           そのとき返ってくるのは移動先の組織で、いま持っている org は
           削除済みになっている。差し替えないと、以降の読み取りが
           消えた組織IDで走り、画面は旧組織名のまま空になる。 */
        if (probe.organization && probe.organization.id !== org.id) {
          org = probe.organization;
          setOrganization(org);
          if (probe.user) {
            const merged = mapUserProfile(probe.user);
            if (merged) setAuthenticatedUser(merged, org);
          }
        } else if (probe.error) {
          /* **自動合流に失敗したことを黙らせない。**

             移すものが無い人（＝試しにログインしただけの空組織）は
             確認画面を出さずにサーバーが合流を試みる。招待元の席が
             足りなければサーバーは理由つきで断るが、それを読んでいないと
             「ログインしたのに何も起きず、自分の1人組織が出るだけ」に
             なる。招待された本人には何が起きたのか分からない。

             招待自体が無いときは error も入らないので、ここに来るのは
             「招待はあるのに合流できなかった」場合だけ。 */
          setInviteError(probe.error);
        }
      }

      // If no organization found, provision new user via Edge Function
      if (!org) {
        logger.log('No organization found, provisioning new user via Edge Function');

        const { organization: newOrg, user: newUser, created, error: provisionError } = await provisionUser();

        if (provisionError || !newOrg) {
          logger.error('Failed to provision user:', provisionError);
          /* provisionUser は必ず利用者向けの日本語を返す。ここで
             「しばらくしてからお試しください」を機械的に足すと、席不足のように
             待っても解決しない事象に誤った案内をしてしまう。そのまま出す。 */
          setDataError(provisionError ?? 'アカウントの作成に失敗しました。しばらくしてからお試しください。');
          setIsDataLoading(false);
          return;
        }

        org = newOrg;
        // 新規プロビジョニング直後は syncAuthenticatedUser がプロフィールを見つけられて
        // いない（まだ存在しなかった）。ここで組織と一緒に認証状態を確定させる。
        const provisionedUser = newUser ? mapUserProfile(newUser) : null;
        if (provisionedUser) {
          setAuthenticatedUser(provisionedUser, org);
        } else {
          setOrganization(org);
        }

        if (created) {
          logger.log('Created new organization and user profile:', newOrg.id, newUser?.id);
          addToast({
            type: 'success',
            message: '新しいアカウントを作成しました'
          });
        } else {
          logger.log('Found existing organization:', newOrg.id);
        }
      }

      // Load all data in parallel
      setIsUsageLoading(true);
      const [casesData, qrPointsData, teamsData, usageData] = await Promise.all([
        getCases(org.id),
        getQRPoints(org.id),
        getTeams(org.id),
        getUsageStats(org.id)
      ]);

      setCases(casesData);
      setQRPoints(qrPointsData);
      setTeams(teamsData);
      setUsageStats(usageData);
      setIsUsageLoading(false);

    } catch (error) {
      logger.error('Failed to load dashboard data:', error);
      setDataError('データの読み込みに失敗しました');
    } finally {
      setIsDataLoading(false);
    }
  }, [organization, setCases, setOrganization, setAuthenticatedUser, setQRPoints, setTeams, addToast]);

  // Load data when entering manager views
  // ポスター画面もログイン済みなら QR ポイントが必要（実トークンを埋め込むため）。
  useEffect(() => {
    if (view.startsWith('manager_')) {
      loadDashboardData();
    } else if (view === 'posters' && isAuthenticated) {
      loadDashboardData();
    }
  }, [view, isAuthenticated, loadDashboardData]);


  /* 運営者かどうか。サイドバーの出し分けにだけ使う。
     データ側の認可は RPC の中で行われるので、ここが false でも情報は漏れない。 */
  const [isOperator, setIsOperator] = useState(false);

  // Worker flow state
  const [submissionData, setSubmissionData] = useState<SubmissionData | null>(null);

  // Re-fetch QR context if it's missing but we have a token
  // NOTE: This must be defined BEFORE the useEffect that uses it
  const ensureQRContext = useCallback(async (): Promise<boolean> => {
    // If we already have qrContext, we're good
    if (qrContext?.organizationId) {
      logger.log('ensureQRContext: qrContext exists', qrContext.organizationId);
      return true;
    }

    // Try to get token from multiple sources
    let token = currentQRToken || window.location.pathname.match(/^\/report\/([a-zA-Z0-9]+)$/)?.[1];

    // Fallback: Try sessionStorage
    if (!token) {
      try {
        token = sessionStorage.getItem('genbasafeai_qr_token') || undefined;
        if (token) {
          logger.log('ensureQRContext: Recovered token from sessionStorage:', token);
          setCurrentQRToken(token);
        }
      } catch (e) {
        logger.warn('ensureQRContext: Failed to read from sessionStorage:', e);
      }
    }

    // Also try to recover context from sessionStorage first (faster than API call)
    try {
      const storedContext = sessionStorage.getItem('genbasafeai_qr_context');
      if (storedContext) {
        const parsedContext = JSON.parse(storedContext);
        if (parsedContext.organizationId) {
          logger.log('ensureQRContext: Recovered context from sessionStorage');
          setQRContext(parsedContext);

          // Refresh stats in background
          getOrganizationMonthlyStats(parsedContext.organizationId).then(stats => {
            setWorkerStats(stats);
          }).catch(err => {
            logger.warn('ensureQRContext: Failed to refresh stats:', err);
          });

          return true;
        }
      }
    } catch (e) {
      logger.warn('ensureQRContext: Failed to parse stored context:', e);
    }

    if (!token) {
      logger.error('ensureQRContext: No token available from any source');
      return false;
    }

    logger.log('ensureQRContext: Refetching QR point for token:', token);

    try {
      const qrPoint = await getQRPointByToken(token);
      if (qrPoint && qrPoint.isActive) {
        const newContext = {
          token,
          qrPointId: qrPoint.id,
          qrPointName: qrPoint.name,
          teamName: qrPoint.name,
          organizationId: qrPoint.organizationId,
          teamId: qrPoint.teamId || ''
        };
        setQRContext(newContext);

        // Store in sessionStorage for future recovery
        try {
          sessionStorage.setItem('genbasafeai_qr_context', JSON.stringify(newContext));
        } catch (e) {
          logger.warn('ensureQRContext: Failed to save to sessionStorage:', e);
        }

        // Also refresh stats
        const stats = await getOrganizationMonthlyStats(qrPoint.organizationId);
        setWorkerStats(stats);

        return true;
      } else {
        logger.error('ensureQRContext: QR point not found or inactive');
        return false;
      }
    } catch (error) {
      logger.error('ensureQRContext: Error refetching QR point:', error);
      return false;
    }
  }, [qrContext, currentQRToken, setQRContext]);

  // Ensure qrContext is valid when entering worker views (skip in demo mode)
  useEffect(() => {
    const isDemoMode = !currentQRToken;
    const checkQRContext = async () => {
      if (view.startsWith('worker_') && !qrContext?.organizationId && !isDemoMode) {
        logger.warn('Worker view entered but qrContext missing, attempting to restore...');
        const success = await ensureQRContext();
        if (!success && view !== 'worker_landing') {
          logger.error('Failed to restore qrContext, redirecting to landing');
          addToast({
            type: 'error',
            message: 'セッションが切れました。QRコードを再度読み取ってください。'
          });
          setView('landing');
        }
      }
    };
    checkQRContext();
  }, [view, qrContext?.organizationId, currentQRToken, ensureQRContext, addToast]);

  /* AI月次利用回数（「8/10回」）の取得はやめた。
   *
   * 匿名の作業員のブラウザから genbasafeai.organizations.plan を読む必要があり、
   * migration 019 で anon の権限を剥奪した結果 42501 で失敗していた（本番ログで確認）。
   * getMonthlyAiUsage は null を返すだけなので画面上は何も起きず、静かに壊れていた。
   *
   * 復活させずに削除した理由:
   *   - QRを読んだだけの匿名の人に、その組織の契約消費状況を見せる必要がない
   *   - 上限到達時の扱いは送信時に処理済み（LIMIT_EXCEEDED で定型文のケースを作る）
   *   - 作業員に見せたいのは「今月このQR/現場から何件投稿されたか」であり、
   *     それは WorkerHome の monthPosts（get_org_monthly_case_stats 経由、anon可）が担う
   */

  // --- Worker Flow Handlers ---
  const handleWorkerInputSubmit = (data: SubmissionData) => {
    setSubmissionData(data);
    // Always show confirmation screen so user can review AI analysis
    setView('worker_confirm');
  };

  const handleWorkerConfirm = async (analysis: AIAnalysisResult): Promise<boolean> => {
    logger.log('handleWorkerConfirm called:', {
      hasSubmissionData: !!submissionData,
      analysisTitle: analysis?.title,
      qrContextAvailable: !!qrContext,
      qrContextOrgId: qrContext?.organizationId,
      hasImageBlob: !!submissionData?.input.imageBlob
    });

    if (!submissionData) {
      logger.error('handleWorkerConfirm: No submission data available');
      logError('handleWorkerConfirm: no submissionData', {}, 'App');
      return false;
    }

    const hasAudio = !!submissionData.input.audioBlob;
    const imageBlob = submissionData.input.imageBlob;

    return handleFinalSubmit(analysis, submissionData.input.text, hasAudio, imageBlob);
  };

  const handleFinalSubmit = async (
    analysis: AIAnalysisResult,
    originalText: string,
    hasAudio: boolean,
    imageBlob?: Blob
  ): Promise<boolean> => {
    logger.log('handleFinalSubmit called:', {
      hasQrContext: !!qrContext,
      qrContextOrgId: qrContext?.organizationId,
      analysisTitle: analysis?.title,
      currentQRToken
    });

    // Try to ensure we have qrContext
    let orgId = qrContext?.organizationId;
    let qrPointId = qrContext?.qrPointId;
    let teamId = qrContext?.teamId;

    // Fallback 1: Try sessionStorage
    if (!orgId) {
      try {
        const storedContext = sessionStorage.getItem('genbasafeai_qr_context');
        if (storedContext) {
          const parsedContext = JSON.parse(storedContext);
          if (parsedContext.organizationId) {
            logger.log('handleFinalSubmit: Recovered context from sessionStorage');
            orgId = parsedContext.organizationId;
            qrPointId = parsedContext.qrPointId;
            teamId = parsedContext.teamId;
            setQRContext(parsedContext);
          }
        }
      } catch (e) {
        logger.warn('handleFinalSubmit: Failed to parse stored context:', e);
      }
    }

    // Fallback 2: Re-fetch from API
    const tokenToUse = currentQRToken || sessionStorage.getItem('genbasafeai_qr_token');
    if (!orgId && tokenToUse) {
      logger.warn('qrContext missing, attempting to re-fetch from API...');
      try {
        const qrPoint = await getQRPointByToken(tokenToUse);
        if (qrPoint && qrPoint.isActive) {
          orgId = qrPoint.organizationId;
          qrPointId = qrPoint.id;
          teamId = qrPoint.teamId || undefined;
          logger.log('Re-fetched QR point data:', { orgId, qrPointId, teamId });

          // Update the store for future use
          const newContext = {
            token: tokenToUse,
            qrPointId: qrPoint.id,
            qrPointName: qrPoint.name,
            teamName: qrPoint.name,
            organizationId: qrPoint.organizationId,
            teamId: qrPoint.teamId || ''
          };
          setQRContext(newContext);

          // Also save to sessionStorage
          try {
            sessionStorage.setItem('genbasafeai_qr_context', JSON.stringify(newContext));
          } catch (e) {
            logger.warn('handleFinalSubmit: Failed to save to sessionStorage:', e);
          }
        }
      } catch (error) {
        logger.error('Failed to re-fetch QR point:', error);
      }
    }

    /* 現場（team）は **QR に紐づいたものだけ** を使う。
     *
     * 以前はここで teamId が空のとき `teams[0].id`（組織の先頭の現場）を
     * 黙って割り当てていた。genbasafeai.teams が 0 行の間は死んでいたが、
     * 工事現場の機能を出した瞬間に有効化され、
     * **現場未設定の QR から届いた匿名報告が、無関係な先頭の現場に
     * 誤って記録される**。しかも logger.log が出るだけで、
     * 管理者の画面には正常な報告として並ぶ。気づけない。
     *
     * 報告は「いつ・どこで起きたか」の記録であり、場所を推測で埋めては
     * いけない。QR に現場が設定されていなければ「現場未設定」のまま保存する。
     * create_case_via_qr は team_id が NULL でも受け付ける（migration 013）。
     */
    if (!teamId || teamId === '') {
      logger.log('teamId is empty: この報告は現場未設定として保存する');
    }

    logger.log('Final submit with:', { orgId, qrPointId, teamId, hasTeamId: !!teamId });

    if (!orgId) {
      // Demo mode: skip DB save and show success screen.
      // ただし worker_success の「深掘り分析」CTA を表示するため、
      // mock の lastSubmittedCase に投稿本文を含めて渡す.
      if (!currentQRToken) {
        logger.log('Demo mode: skipping DB save, showing success');
        setLastSubmittedCase({
          caseId: 'demo',
          title: analysis.title || '報告内容',
          body: analysis.formattedBody || originalText,
          viewerToken: undefined,
        });
        clearReportDraft();
        setView('worker_success');
        return true;
      }
      logger.error('No organization ID found even after re-fetch', { qrContext, currentQRToken });
      addToast({
        type: 'error',
        message: 'QRコードの情報が取得できませんでした。再度QRコードを読み取ってください。'
      });
      setSubmissionData(null);
      setView('worker_landing');
      return false;
    }

    // Validate analysis result
    if (!analysis || !analysis.title || !analysis.formattedBody) {
      logger.error('Invalid analysis result:', {
        hasAnalysis: !!analysis,
        title: analysis?.title,
        formattedBody: analysis?.formattedBody?.substring(0, 50)
      });
      addToast({
        type: 'error',
        message: 'AIの分析結果を受け取れませんでした。入力内容は残っています。もう一度「次へ」を押してください。'
      });
      // submissionData は消さない: worker_form に戻ったとき
      // WorkerInput が initialInput として入力（テキスト・音声・写真）を復元する。
      setView('worker_form');
      return false;
    }

    logger.log('Saving case to database:', {
      orgId,
      qrPointId,
      title: analysis.title,
      bodyLength: analysis.formattedBody.length
    });

    // Upload image FIRST if present (before creating case)
    // 写真のアップロードに失敗しても報告そのものは中断しない（写真が無くても安全報告には価値がある）。
    // ただし黙って捨てない: 2025-12〜2026-08 の 8 か月、ストレージポリシー起因で写真が
    // 1 枚も保存されていなかったのに誰も気付けなかった。必ず作業者とログの両方に出す。
    let uploadedImageUrl: string | undefined;
    let imageUploadFailed = false;
    if (imageBlob && orgId) {
      // Generate a unique ID for the image path
      const imageId = crypto.randomUUID();
      logger.log('Uploading image before case creation:', imageId);
      try {
        const imageUrl = await uploadCaseImage(orgId, imageId, imageBlob);
        if (imageUrl) {
          logger.log('Image uploaded successfully:', imageUrl);
          uploadedImageUrl = imageUrl;
        } else {
          imageUploadFailed = true;
          const uploadErr = getLastImageUploadError();
          logger.error('Image upload returned null', uploadErr);
          logError('handleFinalSubmit: image upload failed', {
            orgId,
            imageId,
            size: imageBlob.size,
            reason: uploadErr?.message || 'unknown',
            stage: uploadErr?.stage || 'unknown'
          }, 'App');
        }
      } catch (uploadError) {
        imageUploadFailed = true;
        logger.error('Image upload failed:', uploadError);
        logError('handleFinalSubmit: image upload threw', {
          orgId,
          imageId,
          size: imageBlob.size,
          reason: uploadError instanceof Error ? uploadError.message : String(uploadError)
        }, 'App');
        // Continue without image
      }
    }

    // Save to database (with imageUrl if available)
    const savedCase = await createCase({
      organizationId: orgId,
      teamId,
      qrPointId,
      title: analysis.title,
      body: analysis.formattedBody,
      originalText: originalText,
      riskLevel: analysis.riskLevel,
      category1: analysis.category1,
      category2: analysis.category2,
      lesson: analysis.lesson,
      hasAudio,
      imageUrl: uploadedImageUrl
    });

    if (savedCase) {
      logger.log('Case saved successfully:', savedCase.id);
      trackCaseSubmitted({
        qrPointId: savedCase.qrPointId,
        category: savedCase.category1,
        riskLevel: savedCase.riskLevel,
        hasAudio: !!savedCase.hasAudio,
        hasImage: !!savedCase.imageUrl,
      });

      // Add to local state as well
      addCase(savedCase);
      // Update worker stats to reflect new submission
      setWorkerStats(prev => ({
        ...prev,
        monthPosts: prev.monthPosts + 1
      }));
      // 投稿者本人が後で状況確認するための viewer_token を控えて WorkerSuccess に渡す
      setLastSubmittedCase({
        caseId: savedCase.id,
        title: savedCase.title,
        body: savedCase.body || savedCase.originalText,
        viewerToken: savedCase.viewerToken,
        imageUploadFailed,
      });

      // 成功と失敗を切り分けて伝える。報告は保存済みなので error ではなく warning。
      if (imageUploadFailed) {
        addToast({
          type: 'warning',
          message: '報告は送信できましたが、写真は添付できませんでした。',
          duration: 8000
        });
      }

      setSubmissionData(null);
      clearReportDraft();
      setView('worker_success');
      return true;
    } else {
      const dbError = getLastDbError();
      const errorDetails = {
        orgId,
        qrPointId,
        dbError: dbError ? { message: dbError.message, code: dbError.code } : null
      };
      logger.error('Failed to save case to database', errorDetails);
      logError('handleWorkerConfirm: DB save failed', errorDetails, 'App');

      // 作業員に技術文言（英語メッセージ・エラーコード）は見せない。
      // 画面遷移もしない: worker_confirm に留まり、WorkerConfirm が
      // インラインエラー + 「送信する」での再試行導線を出す。
      // worker_form に戻すと再送に AI 分析の再実行（クォータ消費）が必要になり、
      // 確認画面での編集内容（editedAnalysis）も失われるため。
      // submissionData はこのとき保持されているので「戻る」での入力復元も生きている。
      return false;
    }
    return true;
  };

  const resetWorkerFlow = () => {
    setSubmissionData(null);
    setView('worker_form');
  };

  // --- QR Management Handlers ---
  const handleCreateQR = async (name: string, teamId?: string, equipmentInfo?: string) => {
    if (!organization) return null;
    const qr = await createQRPoint(organization.id, name, teamId, equipmentInfo);
    if (qr) {
      setQRPoints([...qrPoints, qr]);
      // Update organization qr_used count
      setOrganization({
        ...organization,
        qrUsed: organization.qrUsed + 1
      });
    }
    return qr;
  };

  const handleDeleteQR = async (id: string) => {
    const success = await deleteQRPoint(id);
    if (success) {
      setQRPoints(qrPoints.filter(q => q.id !== id));
      // Update organization qr_used count
      if (organization) {
        setOrganization({
          ...organization,
          qrUsed: Math.max(0, organization.qrUsed - 1)
        });
      }
      addToast({ type: 'success', message: 'QRコードを削除しました' });
    } else {
      // 失敗が無音だと「消えたつもり」で掲示を剥がしてしまう
      addToast({
        type: 'error',
        message: 'QRコードの削除に失敗しました。通信環境を確認して、もう一度お試しください。'
      });
    }
  };

  const handleToggleQR = async (id: string, isActive: boolean) => {
    const success = await updateQRPoint(id, { isActive });
    if (success) {
      setQRPoints(qrPoints.map(q => q.id === id ? { ...q, isActive } : q));
      addToast({
        type: 'success',
        message: isActive ? 'QRコードを有効にしました' : 'QRコードを無効にしました。再有効化はいつでもできます'
      });
    } else {
      addToast({
        type: 'error',
        message: 'QRコードの状態変更に失敗しました。通信環境を確認して、もう一度お試しください。'
      });
    }
  };

  const handleEditQR = async (id: string, updates: { name?: string; equipmentInfo?: string; teamId?: string | null }): Promise<boolean> => {
    /* teamId だけの呼び出しは「保存済みの値を画面に反映するだけ」の合図。
       DB は setQRPointTeam が既に更新しているので、ここで再度書かない
       （同じ値の二重更新を避ける）。 */
    if (updates.teamId !== undefined && updates.name === undefined && updates.equipmentInfo === undefined) {
      setQRPoints(qrPoints.map(q => q.id === id ? { ...q, teamId: updates.teamId ?? undefined } : q));
      return true;
    }
    const success = await updateQRPoint(id, updates);
    if (success) {
      setQRPoints(qrPoints.map(q => q.id === id ? { ...q, ...updates } : q));
    }
    return success;
  };

  // Handle case status update (persists to DB and updates local state)
  const handleUpdateCase = async (id: string, updates: Partial<Case>) => {
    // If status is being updated, persist to database
    if (updates.status) {
      const success = await updateCaseStatus(id, updates.status);
      if (!success) {
        addToast({ type: 'error', message: 'ステータスの更新に失敗しました' });
        return;
      }
      // 解決済み (PDCA の Check/Action 完遂) を計測
      if (updates.status === 'resolved') {
        const target = cases.find((c) => c.id === id);
        trackCaseResolved({
          caseId: id,
          category: target?.category1,
          riskLevel: target?.riskLevel,
        });
      }
    }
    // Update local state
    updateCase(id, updates);
    addToast({ type: 'success', message: 'ステータスを更新しました' });
  };

  // --- Common Components ---
  const Navbar = () => (
    <nav className="no-print sticky top-0 z-50 w-full bg-white/90 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => navigateTo('landing')}
          >
            <img src="/icon-512x512.png" alt="" className="h-8 w-8" />
            <span className="text-xl font-bold tracking-tight text-slate-900 whitespace-nowrap">
              安全ポスト＋
            </span>
          </div>

          {/* Desktop Nav
              768px には7項目が物理的に入らず、語中で改行して崩れていた（実測）。
              nowrap だけ付けると今度は横にはみ出すので、境界自体を lg(1024px) に上げる。 */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <button
              onClick={() => navigateTo('landing')}
              className={`text-sm font-medium hover:text-safety-600 transition whitespace-nowrap py-2 ${
                view === 'landing' ? 'text-safety-600' : 'text-slate-600'
              }`}
            >
              ホーム
            </button>
            <button
              onClick={() => navigateTo('guide')}
              className={`text-sm font-medium hover:text-safety-600 transition whitespace-nowrap py-2 ${
                view === 'guide' ? 'text-safety-600' : 'text-slate-600'
              }`}
            >
              使い方
            </button>
            <button
              onClick={() => navigateTo('posters')}
              className={`text-sm font-medium hover:text-safety-600 transition whitespace-nowrap py-2 ${
                view === 'posters' ? 'text-safety-600' : 'text-slate-600'
              }`}
            >
              掲示ポスター
            </button>
            <button
              onClick={() => navigateTo('pricing')}
              className={`text-sm font-medium hover:text-safety-600 transition whitespace-nowrap py-2 ${
                view === 'pricing' ? 'text-safety-600' : 'text-slate-600'
              }`}
            >
              料金
            </button>
            <a
              href="/blog/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-slate-600 hover:text-safety-600 transition whitespace-nowrap py-2"
            >
              ブログ
            </a>
            <div className="flex items-center space-x-4 ml-2 xl:ml-4">
              <button
                onClick={() => setView('worker_landing')}
                className="text-sm font-bold text-slate-900 hover:text-safety-600 transition whitespace-nowrap py-2"
              >
                デモ体験（現場用）
              </button>
              {/* ログイン中に「ログイン / 新規登録」を出すと、現に持っているセッションを
                  否定する表示になる（押しても checkSession がダッシュボードへ跳ね返す）。
                  認証状態を見て管理画面への帰り道に差し替える。 */}
              {isAuthenticated ? (
                <button
                  onClick={() => setView('manager_dashboard')}
                  className="bg-industrial-900 text-white px-5 py-2 rounded-full text-sm font-bold hover:bg-slate-800 transition shadow-lg shadow-slate-900/20 whitespace-nowrap"
                >
                  管理画面へ
                </button>
              ) : (
                <button
                  onClick={() => {
                    trackSignupStarted('navbar');
                    navigateTo('login');
                  }}
                  className="bg-industrial-900 text-white px-5 py-2 rounded-full text-sm font-bold hover:bg-slate-800 transition shadow-lg shadow-slate-900/20 whitespace-nowrap"
                >
                  ログイン / 新規登録
                </button>
              )}
            </div>
          </div>

          {/* Mobile Menu Button: ナビ全項目が収まらない <1024px はハンバーガーに寄せる */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-slate-600 p-2.5 -m-2.5"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'メニューを閉じる' : 'メニューを開く'}
            >
              {isMobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 p-4 space-y-2 shadow-xl">
          <button
            className="block text-base font-medium text-slate-600 w-full text-left py-2.5"
            onClick={() => {
              navigateTo('landing');
              setIsMobileMenuOpen(false);
            }}
          >
            ホーム
          </button>
          <button
            className="block text-base font-medium text-slate-600 w-full text-left py-2.5"
            onClick={() => {
              navigateTo('guide');
              setIsMobileMenuOpen(false);
            }}
          >
            使い方
          </button>
          <button
            className="block text-base font-medium text-slate-600 w-full text-left py-2.5"
            onClick={() => {
              navigateTo('posters');
              setIsMobileMenuOpen(false);
            }}
          >
            掲示ポスター（無料・印刷用）
          </button>
          <button
            className="block text-base font-medium text-slate-600 w-full text-left py-2.5"
            onClick={() => {
              navigateTo('pricing');
              setIsMobileMenuOpen(false);
            }}
          >
            料金
          </button>
          <a
            href="/blog/"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-base font-medium text-slate-600 w-full text-left py-2.5"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            ブログ
          </a>
          <div className="pt-4 border-t border-slate-100 flex flex-col space-y-3">
            <button
              onClick={() => {
                setView('worker_landing');
                setIsMobileMenuOpen(false);
              }}
              className="w-full text-center py-3 border border-slate-300 rounded-lg font-bold"
            >
              QRデモ体験
            </button>
            <button
              onClick={() => {
                if (isAuthenticated) {
                  setView('manager_dashboard');
                } else {
                  navigateTo('login');
                }
                setIsMobileMenuOpen(false);
              }}
              className="w-full text-center py-3 bg-industrial-900 text-white rounded-lg font-bold"
            >
              {isAuthenticated ? '管理画面へ' : '管理者ログイン'}
            </button>
          </div>
        </div>
      )}
    </nav>
  );

  const Footer = () => (
    <footer className="no-print bg-industrial-900 text-slate-400 py-16 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="col-span-2">
            <div
              className="flex items-center space-x-2 mb-6 cursor-pointer"
              onClick={() => navigateTo('landing')}
            >
              <img src="/icon-512x512.png" alt="" className="h-7 w-7 rounded-md" />
              <span className="text-xl font-bold text-white">安全ポスト＋</span>
            </div>
            <p className="text-sm leading-relaxed max-w-sm mb-6">
              現場の安全文化を醸成するAIプラットフォーム。
              <br />
              「怒られない」報告が、明日の命を守ります。
              <br />
              {/* 旧: 「建設・製造・物流現場での導入実績多数。」
                  実績件数を裏づける公表可能な根拠がなく、景品表示法上の優良誤認にあたりうる
                  ため、仕様から検証できる事実（QRで匿名報告／AIが4M分析／登録不要で無料）に
                  置き換えた。実績数・No.1・事故ゼロ等の訴求は今後も使わない。 */}
              QRで匿名報告、AIが4M分析。
              <br />
              登録不要・カード不要で無料でお試しいただけます。
            </p>
          </div>
          <div>
            <h4 className="text-white font-bold mb-6">Product</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <button onClick={() => navigateTo('landing')} className="hover:text-white transition">
                  ホーム
                </button>
              </li>
              <li>
                <button onClick={() => navigateTo('guide')} className="hover:text-white transition">
                  使い方ガイド
                </button>
              </li>
              <li>
                <button onClick={() => navigateTo('posters')} className="hover:text-white transition">
                  安全掲示ポスター（無料）
                </button>
              </li>
              <li>
                <button onClick={() => navigateTo('pricing')} className="hover:text-white transition">
                  料金プラン
                </button>
              </li>
              <li>
                <button onClick={() => navigateTo('why_demo')} className="hover:text-white transition">
                  4Mなぜなぜ分析 デモ
                </button>
              </li>
              <li>
                {/* Navbar と同じ理由でログイン状態を見る。フッターだけ取り残すと
                    ログイン中の全公開ページに「ログイン / 新規登録」が残る。 */}
                <button
                  onClick={() => (isAuthenticated ? setView('manager_dashboard') : navigateTo('login'))}
                  className="hover:text-white transition"
                >
                  {isAuthenticated ? '管理画面へ' : 'ログイン / 新規登録'}
                </button>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold mb-6">Legal & Support</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <button onClick={() => navigateTo('about')} className="hover:text-white transition">
                  運営者プロフィール
                </button>
              </li>
              <li>
                <button onClick={() => navigateTo('legal_about')} className="hover:text-white transition">
                  運営者情報（事業者）
                </button>
              </li>
              <li>
                <button onClick={() => navigateTo('legal_privacy')} className="hover:text-white transition">
                  プライバシーポリシー
                </button>
              </li>
              <li>
                <button onClick={() => navigateTo('legal_transactions')} className="hover:text-white transition">
                  特定商取引法に基づく表記
                </button>
              </li>
              <li>
                <button onClick={() => navigateTo('legal_contact')} className="hover:text-white transition">
                  お問い合わせ
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center">
          <p className="text-xs text-slate-500">
            &copy; 2025 安全ポスト＋ All rights reserved.
          </p>
          <div className="flex items-center space-x-2 mt-4 md:mt-0">
            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
            <span className="text-xs text-slate-500">System Operational</span>
          </div>
        </div>
      </div>
    </footer>
  );

  // --- Routing Logic ---

  // /my-report/{token} で開かれた匿名閲覧画面。auth state とは独立して動く。
  if (view === 'qr_invalid' && qrInvalid) {
    return (
      <QrInvalidView
        reason={qrInvalid.reason}
        token={qrInvalid.token}
        onRetry={() => {
          // 同じトークンでルーティングをやり直す
          window.location.href = `/report/${qrInvalid.token}`;
        }}
        onGoHome={() => {
          setQrInvalid(null);
          navigateTo('landing');
        }}
      />
    );
  }

  if (view === 'my_report' && myReportToken) {
    return (
      <MyReportView
        token={myReportToken}
        onBack={() => {
          setMyReportToken(null);
          navigateTo('landing');
        }}
      />
    );
  }

  // /my-reports 自分の投稿一覧 (localStorage ベース、auth 不要)
  if (view === 'my_reports_list') {
    return (
      <MyReportsListView
        onBack={() => navigateTo('landing')}
        onOpenReport={(token) => {
          setMyReportToken(token);
          setView('my_report');
          window.history.pushState({ view: 'my_report' }, '', `/my-report/${token}`);
        }}
      />
    );
  }

  // Show auth error screen (expired token, timeout, etc.)
  if (authError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-slate-800 mb-2">ログインできませんでした</h2>
          <p className="text-sm text-slate-600 mb-6">{authError}</p>
          <button
            onClick={() => {
              setAuthError(null);
              setView('login');
            }}
            className="w-full bg-safety-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-safety-700 transition-colors"
          >
            ログイン画面に戻る
          </button>
          <p className="text-xs text-slate-400 mt-4">
            問題が解決しない場合は support@genbacompass.com までご連絡ください
          </p>
        </div>
      </div>
    );
  }

  // Show loading screen while authenticating via MagicLink
  if (isAuthenticating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <img src="/icon-512x512.png" alt="" className="h-14 w-14 mx-auto mb-4 animate-pulse rounded-xl" />
          <Loader2 className="h-8 w-8 animate-spin text-safety-600 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">ログイン中...</p>
        </div>
      </div>
    );
  }

  // Show in-app browser warning
  if (inAppBrowserWarning?.show) {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);

    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Smartphone className="h-8 w-8 text-orange-600" />
            </div>
            <h1 className="text-xl font-bold text-industrial-900 mb-2">
              外部ブラウザで開いてください
            </h1>
            <p className="text-slate-600 text-sm">
              アプリ内ブラウザでは正常に動作しない場合があります。
              Safari や Chrome で開き直してください。
            </p>
          </div>

          <div className="space-y-4">
            {/* URL Copy Button */}
            <button
              onClick={() => copyUrlToClipboard(inAppBrowserWarning.url)}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold transition ${
                urlCopied
                  ? 'bg-emerald-500 text-white'
                  : 'bg-industrial-900 text-white hover:bg-slate-800'
              }`}
            >
              {urlCopied ? (
                <>
                  <CheckCircle className="h-5 w-5" />
                  コピーしました！
                </>
              ) : (
                <>
                  <Copy className="h-5 w-5" />
                  URLをコピー
                </>
              )}
            </button>

            {/* Instructions */}
            <div className="bg-slate-50 rounded-xl p-4">
              <h3 className="font-bold text-industrial-900 mb-3 text-sm">
                開き方：
              </h3>
              <ol className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-industrial-900 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    1
                  </span>
                  <span>上の「URLをコピー」をタップ</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-industrial-900 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    2
                  </span>
                  <span>
                    {isIOS
                      ? 'Safari を開く'
                      : isAndroid
                      ? 'Chrome を開く'
                      : 'ブラウザアプリを開く'}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-industrial-900 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    3
                  </span>
                  <span>アドレスバーに貼り付けてアクセス</span>
                </li>
              </ol>
            </div>

            {/* Alternative: Open in browser (some apps support this) */}
            <a
              href={inAppBrowserWarning.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-slate-200 rounded-xl text-slate-700 font-medium hover:bg-slate-50 transition"
            >
              <ExternalLink className="h-5 w-5" />
              外部ブラウザで開く
            </a>

            {/* Continue anyway button (for testing) */}
            <button
              onClick={() => {
                setInAppBrowserWarning(null);
                // Re-trigger URL routing without in-app check
                window.location.reload();
              }}
              className="w-full text-center text-sm text-slate-400 hover:text-slate-600 transition py-2"
            >
              このまま続ける（動作しない可能性があります）
            </button>
          </div>

          {/* URL Display */}
          <div className="mt-4 p-3 bg-slate-100 rounded-lg break-all text-xs text-slate-500 font-mono">
            {inAppBrowserWarning.url}
          </div>
        </div>
      </div>
    );
  }

  // Handler for successful login
  const handleLoginSuccess = () => {
    const userObj = (useAppStore.getState().user as unknown as { id?: string }) || {};
    const orgObj = (useAppStore.getState().organization as unknown as { createdAt?: string }) || {};
    // 「新規組織」か「既存組織」かを createdAt 経過時間で粗く推定 (5分以内なら新規扱い)
    const isNewOrg = !!orgObj.createdAt && Date.now() - new Date(orgObj.createdAt).getTime() < 5 * 60 * 1000;
    trackSignupCompleted({ isNewOrg, userId: userObj.id || 'unknown' });
    setView('manager_dashboard');
  };

  // 0. Login View
  if (view === 'login') {
    return (
      <LoginView
        onSuccess={handleLoginSuccess}
        onBack={() => setView('landing')}
      />
    );
  }

  // 0b. 安全掲示ポスター（未ログインでも閲覧・印刷できる公開ページ）
  //
  //  ログイン中は管理画面の一機能として使うので、サイドバーの中に出す。
  //  公開Navbarに切り替わると「ログアウトした」ように見えるという指摘があった。
  //  未ログイン時は公開ページの共通ラッパから外して独立レンダーする。理由は印刷:
  //  ラッパに同梱されるチャットウィジェットは fixed 配置で紙面に重なるため、
  //  この画面には載せない（Navbar / Footer は no-print で消える）。
  if (view === 'posters') {
    const gallery = (
      <PosterGalleryView
        isSignedIn={isAuthenticated}
        qrPoints={qrPoints}
        defaultOrgName={organization?.name}
        initialQrPointId={posterQrPointId}
        /* QR管理から来たときは「このQRのポスターを刷る」が目的なので、
           業種も季節も選ばない汎用テーマを開いておく。 */
        initialThemeKey={posterQrPointId ? 'general' : undefined}
        onSignup={() => {
          trackSignupStarted('posters');
          navigateTo('login');
        }}
        onManageQr={() => setView('manager_qr')}
        /* サイドバーが出ているときはページ内の戻り導線を出さない（二重になる）。 */
        onGoHome={isAuthenticated ? undefined : () => navigateTo('landing')}
      />
    );

    if (isAuthenticated) {
      return (
        <ManagerLayout
          currentView={view}
          /* ポスターだけが URL を持つので、離れるときは '/' に戻して
             URL と画面の食い違いを残さない（他の manager_* は viewToPath が '/'）。 */
          onChangeView={(next) => (next === 'posters' ? navigateTo('posters') : navigateTo(next))}
          printable
          isOperator={isOperator}
        >
          {gallery}
        </ManagerLayout>
      );
    }

    return (
      <div className="ap-print-reset min-h-screen bg-slate-100 text-slate-900 font-sans flex flex-col">
        <Navbar />
        <main className="ap-print-reset flex-1">{gallery}</main>
        <Footer />
      </div>
    );
  }

  // Helper to determine legal tab from view
  const getLegalTab = (currentView: ViewState) => {
    switch (currentView) {
      case 'legal_about': return 'about';
      case 'legal_privacy': return 'privacy';
      case 'legal_transactions': return 'transactions';
      case 'legal_contact': return 'contact';
      default: return 'about';
    }
  };

  // 1. Public Pages (Landing, Guide, Legal, Pricing)
  if (['landing', 'about', 'why_demo', 'guide', 'legal', 'legal_about', 'legal_privacy', 'legal_transactions', 'legal_contact', 'pricing'].includes(view)) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-safety-500 selection:text-white flex flex-col">
        <Navbar />

        <main className="flex-1">
          {view === 'landing' && (
            <>
              {/* Hero Section（Claude Design 刷新: 何のサービスか一目で伝える） */}
              <section className="relative overflow-hidden" style={{ padding: 'clamp(48px,7vw,88px) clamp(16px,4vw,40px) clamp(40px,6vw,72px)' }}>
                <div aria-hidden="true" className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
                  {/* 全業種モンタージュ背景（生成画像） */}
                  <img src="/lp/hero/hero-montage.webp" alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: .9 }} />
                  {/* ライト暗幕：写真を淡く沈め、濃色テキストを可読に（design準拠） */}
                  <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 94% 86% at 50% 42%, rgba(248,250,252,.96) 0%, rgba(248,250,252,.9) 44%, rgba(248,250,252,.7) 100%)' }} />
                  <div className="animate-pulse-slow" style={{ position: 'absolute', top: 40, left: -40, width: 420, height: 420, borderRadius: '50%', background: 'rgba(249,115,22,.10)', filter: 'blur(80px)' }} />
                </div>

                <div className="relative z-10 text-center" style={{ maxWidth: 980, margin: '0 auto' }}>
                  <div className="inline-flex items-center mb-6" style={{ gap: 8, background: '#fff7ed', border: '1px solid #ffedd5', borderRadius: 999, padding: '6px 14px' }}>
                    <span className="animate-pulse-slow" style={{ width: 8, height: 8, borderRadius: '50%', background: '#f97316' }} />
                    <span style={{ fontSize: 11, fontWeight: 800, color: '#c2410c', letterSpacing: '.08em', textTransform: 'uppercase' }}>現場の安全革命 v1.0</span>
                  </div>

                  <h1 style={{ fontSize: 'clamp(2.2rem,5.4vw,4.4rem)', lineHeight: 1.14, fontWeight: 800, letterSpacing: '-.02em', color: '#0f172a', margin: '0 0 20px' }}>
                    QRの報告を、<br />
                    AIが30秒で<span style={{ background: 'linear-gradient(90deg,#ea580c,#eab308)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>“安全”</span>に変える。
                  </h1>

                  <p style={{ maxWidth: 680, margin: '0 auto', fontSize: 'clamp(1rem,1.5vw,1.25rem)', color: '#475569', lineHeight: 1.7 }}>
                    アプリ不要・ログイン不要。匿名で集めたヒヤリハットを、AIが自動で<strong style={{ color: '#0f172a', fontWeight: 700 }}>匿名化＋4M分析＋改善提案</strong>。建設・製造・物流はもちろん、小売・保育・オフィスまで、<strong style={{ color: '#0f172a', fontWeight: 700 }}>あらゆる現場の改善</strong>に。
                  </p>
                  <p style={{ maxWidth: 680, margin: '10px auto 0', fontSize: 'clamp(11px,1.25vw,13px)', color: '#64748b' }}>
                    ※ ヒヤリハット＝事故には至らなかったが「ヒヤリ」「ハッ」とした危険な気づき。4M＝人・機械・材料・方法の4観点。
                  </p>

                  {/* fact chips（仕様由来の事実のみ） */}
                  <div className="flex flex-wrap justify-center" style={{ gap: 8, marginTop: 22 }}>
                    {['アプリ不要', 'ログイン不要', '30秒で報告', '匿名・多言語対応'].map((label) => (
                      <span key={label} className="inline-flex items-center" style={{ gap: 6, whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600, color: '#334155', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 999, padding: '7px 13px' }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#f97316' }} />{label}
                      </span>
                    ))}
                  </div>

                  {/* CTAs */}
                  <div className="flex flex-wrap justify-center" style={{ gap: 14, marginTop: 30 }}>
                    <button data-cta="hero-try-demo" onClick={() => setView('worker_landing')} className="inline-flex items-center hover:-translate-y-0.5 transition" style={{ gap: 9, padding: '15px 28px', border: 'none', cursor: 'pointer', borderRadius: 13, fontSize: 17, fontWeight: 800, color: '#fff', background: 'linear-gradient(90deg,#f97316,#eab308)', boxShadow: '0 14px 30px -8px rgba(249,115,22,.5)' }}>
                      <QrCode className="h-5 w-5" />
                      今すぐ30秒で試す
                      <ChevronRight className="h-[18px] w-[18px]" />
                    </button>
                    <button data-cta="hero-pricing" onClick={() => navigateTo('pricing')} className="inline-flex items-center hover:bg-slate-50 transition" style={{ gap: 8, padding: '15px 26px', borderRadius: 13, fontSize: 17, fontWeight: 700, color: '#0f172a', background: '#fff', border: '1px solid #e2e8f0', cursor: 'pointer' }}>
                      料金プランを見る
                    </button>
                  </div>
                  <p style={{ margin: '14px 0 0', fontSize: 13, color: '#64748b' }}>登録不要・カード不要・30秒で「報告 → AI分析 → 管理画面」の流れを体感できます</p>
                  <div style={{ marginTop: 6 }}>
                    <DeckDownloadCTA placement="landing-hero" variant="hero-pill" />
                  </div>

                  {/* 業種ストリップ：あらゆる現場に（design useImages・生成写真4枚） */}
                  <div style={{ marginTop: 'clamp(28px,4vw,40px)' }}>
                    <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.08em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 14 }}>あらゆる現場の“ヒヤリ”と改善に</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                      {[
                        { img: 'hero-construction-manufacturing', label: '建設・製造' },
                        { img: 'hero-retail-supermarket', label: '物流・小売' },
                        { img: 'hero-care-daycare', label: '保育・介護' },
                        { img: 'hero-office', label: 'オフィス' },
                      ].map((t) => (
                        <div key={t.img} style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', aspectRatio: '16 / 10', boxShadow: '0 8px 22px -12px rgba(15,23,42,.3)' }}>
                          <img src={`/lp/hero/${t.img}.webp`} alt={`${t.label}の現場`} className="absolute inset-0 w-full h-full object-cover" />
                          <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '18px 12px 8px', fontSize: 13, fontWeight: 800, color: '#fff', textAlign: 'left', background: 'linear-gradient(to top,rgba(15,23,42,.78),transparent)' }}>{t.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* HERO VISUAL: スマホ報告 → AI分析 → 管理画面 の3シーン連結 */}
                  <div className="flex items-center justify-center flex-wrap" style={{ marginTop: 'clamp(40px,6vw,72px)', gap: 18 }}>
                    {/* ① スマホで報告 */}
                    <div className="flex flex-col items-center" style={{ gap: 12, flex: '0 0 auto' }}>
                      <div style={{ position: 'relative', width: 152, background: 'linear-gradient(160deg,#334155,#0f172a)', borderRadius: 30, padding: 8, boxShadow: '0 22px 44px -16px rgba(15,23,42,.55)' }}>
                        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 268, background: '#fff', borderRadius: 23, overflow: 'hidden' }}>
                          <div style={{ position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)', width: 52, height: 15, background: '#0f172a', borderRadius: 999, zIndex: 2 }} />
                          <div className="flex items-center justify-between" style={{ flex: '0 0 auto', height: 26, padding: '0 12px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: 8, fontWeight: 700, color: '#475569' }}>9:41</span>
                            <span style={{ width: 13, height: 7, border: '1px solid #94a3b8', borderRadius: 2, display: 'inline-block' }} />
                          </div>
                          <div style={{ flex: '1 1 auto', padding: '13px 11px', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ fontSize: 9, fontWeight: 800, color: '#c2410c' }}>ヒヤリ報告</div>
                            <div style={{ marginTop: 9, height: 6, borderRadius: 3, background: '#e2e8f0' }} />
                            <div style={{ marginTop: 6, height: 6, width: '78%', borderRadius: 3, background: '#e2e8f0' }} />
                            <div style={{ marginTop: 6, height: 6, width: '60%', borderRadius: 3, background: '#e2e8f0' }} />
                            <div className="flex" style={{ marginTop: 14, gap: 6 }}>
                              {['テキスト', '音声', '写真'].map((t) => (
                                <span key={t} style={{ fontSize: 7, fontWeight: 700, color: '#475569', background: '#f1f5f9', borderRadius: 5, padding: '3px 6px' }}>{t}</span>
                              ))}
                            </div>
                            <div style={{ marginTop: 'auto', height: 24, borderRadius: 8, background: 'linear-gradient(90deg,#f97316,#eab308)' }} />
                          </div>
                        </div>
                      </div>
                      <span className="inline-flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: '#475569' }}>
                        <Smartphone className="h-3.5 w-3.5" style={{ color: '#f97316' }} />① スマホで報告
                      </span>
                    </div>

                    <ArrowRight className="h-7 w-7" style={{ color: '#cbd5e1', flex: '0 0 auto' }} />

                    {/* ② AIが匿名化＋4M分析 */}
                    <div className="flex flex-col items-center" style={{ gap: 12, flex: '0 0 auto' }}>
                      <div className="text-left" style={{ width: 248, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: 16, boxShadow: '0 24px 48px -16px rgba(15,23,42,.22)' }}>
                        <div className="flex items-center justify-between" style={{ marginBottom: 11 }}>
                          <span className="inline-flex items-center" style={{ gap: 6, whiteSpace: 'nowrap', fontSize: 12, fontWeight: 800, color: '#0f172a' }}>
                            <Sparkles className="h-[15px] w-[15px]" style={{ color: '#f97316' }} />AI分析結果
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: '#dc2626', borderRadius: 999, padding: '4px 10px' }}>危険度 High</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#334155', background: '#f8fafc', borderRadius: 9, padding: 10, lineHeight: 1.6 }}>「<span style={{ display: 'inline-block', width: 34, height: 11, verticalAlign: -1, background: '#0f172a', borderRadius: 3 }} />さんが3F足場で…」<span style={{ color: '#16a34a', fontWeight: 700, whiteSpace: 'nowrap' }}>✓ 匿名化済</span></div>
                        <div className="flex" style={{ gap: 6, marginTop: 11 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 7, padding: '4px 8px' }}>人</span>
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#c2410c', background: '#fff7ed', border: '1px solid #ffedd5', borderRadius: 7, padding: '4px 8px' }}>機械</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 7, padding: '4px 8px' }}>材料</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 7, padding: '4px 8px' }}>方法</span>
                        </div>
                        <div style={{ marginTop: 11, paddingTop: 11, borderTop: '1px solid #f1f5f9' }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>改善提案</div>
                          <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.6 }}>作業前点検に手すり固定の確認を追加。</div>
                        </div>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>② AIが匿名化＋4M分析</span>
                    </div>

                    <ArrowRight className="h-7 w-7" style={{ color: '#cbd5e1', flex: '0 0 auto' }} />

                    {/* ③ 管理画面(PC)で可視化・追跡 */}
                    <div className="flex flex-col items-center" style={{ gap: 12, flex: '1 1 300px', minWidth: 280, maxWidth: 380 }}>
                      <div style={{ width: '100%' }}>
                        <div style={{ background: '#0f172a', border: '2px solid #334155', borderRadius: '14px 14px 5px 5px', boxShadow: '0 24px 50px -18px rgba(15,23,42,.5)', overflow: 'hidden' }}>
                          <div className="flex items-center" style={{ gap: 8, padding: '8px 12px' }}>
                            <span className="flex" style={{ gap: 5 }}>
                              <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#ef4444' }} />
                              <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#eab308' }} />
                              <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#22c55e' }} />
                            </span>
                            <span className="flex items-center" style={{ flex: 1, gap: 6, height: 18, padding: '0 9px', borderRadius: 999, background: 'rgba(255,255,255,.1)', fontSize: 9, color: '#94a3b8' }}>anzenpost.com/dashboard</span>
                          </div>
                          <div style={{ background: '#f8fafc', padding: 12 }}>
                            <div className="flex" style={{ gap: 8 }}>
                              {[{ l: '今月の報告', v: '42', c: '#0f172a' }, { l: 'High', v: '5', c: '#dc2626' }, { l: '対応済', v: '37', c: '#16a34a' }].map((s) => (
                                <div key={s.l} style={{ flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 8 }}>
                                  <div style={{ fontSize: 9, color: '#64748b' }}>{s.l}</div>
                                  <div style={{ fontSize: 18, fontWeight: 800, color: s.c }}>{s.v}</div>
                                </div>
                              ))}
                            </div>
                            <div className="flex items-end" style={{ gap: 7, height: 54, marginTop: 10, padding: '0 2px' }}>
                              {[{ h: '60%', b: '#fdba74' }, { h: '100%', b: '#f97316' }, { h: '45%', b: '#fdba74' }, { h: '72%', b: '#fb923c' }].map((bar, i) => (
                                <div key={i} style={{ flex: 1, height: bar.h, background: bar.b, borderRadius: '4px 4px 0 0' }} />
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div style={{ width: 50, height: 16, background: 'linear-gradient(180deg,#475569,#334155)' }} />
                          <div style={{ width: 128, height: 8, background: '#334155', borderRadius: '0 0 999px 999px' }} />
                        </div>
                      </div>
                      <span className="inline-flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: '#475569' }}>
                        <Monitor className="h-3.5 w-3.5" style={{ color: '#f97316' }} />③ 管理画面（PC）で可視化・追跡
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* 3-STEP SUMMARY BAND（スクロール前に流れを把握） */}
              <section style={{ padding: 'clamp(36px,5vw,64px) clamp(16px,4vw,40px)', maxWidth: 1280, margin: '0 auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 18 }}>
                  {[
                    { n: '01', Icon: QrCode, title: 'QRで30秒投稿', desc: 'スマホで貼り出されたQRを読むだけ。アプリ不要・ログイン不要で、気づいた「ヒヤリ」をその場で匿名報告。' },
                    { n: '02', Icon: Sparkles, title: 'AIが匿名化＋4M分析', desc: '個人を特定する情報をAIが自動で伏せ字に。4M（人・機械・材料・方法）で分類し、危険度判定と改善提案まで自動生成。' },
                    { n: '03', Icon: BarChart3, title: '管理画面で改善追跡', desc: '集まった報告を可視化。対応状況をトラッキングし、現場の安全文化を継続的に育てる。' },
                  ].map((s) => (
                    <div key={s.n} style={{ position: 'relative', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: 24, boxShadow: '0 1px 2px rgba(15,23,42,.04)' }}>
                      <span style={{ position: 'absolute', top: 18, right: 20, fontSize: 34, fontWeight: 800, color: '#f1f5f9', lineHeight: 1 }}>{s.n}</span>
                      <div className="inline-flex items-center justify-center" style={{ width: 48, height: 48, borderRadius: 13, background: '#fff7ed', color: '#ea580c', marginBottom: 14 }}>
                        <s.Icon className="h-6 w-6" />
                      </div>
                      <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{s.title}</h3>
                      <p style={{ margin: 0, fontSize: 14, color: '#475569', lineHeight: 1.7 }}>{s.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Features Section */}
              <section id="features" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold text-industrial-900 sm:text-4xl">
                      現場の課題をテクノロジーで解決
                    </h2>
                    <p className="mt-4 text-lg text-slate-600">
                      従来の「書かされる報告書」から「活かされるデータ」へ。
                    </p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-12">
                    <div className="group p-8 bg-slate-50 rounded-2xl transition hover:bg-orange-50 hover:shadow-lg border border-transparent hover:border-orange-100">
                      <div className="w-14 h-14 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-safety-600 group-hover:scale-110 transition">
                        <Zap size={32} />
                      </div>
                      <h3 className="text-xl font-bold text-industrial-900 mb-3">
                        最短30秒で報告完了
                      </h3>
                      <p className="text-slate-600 leading-relaxed">
                        スマホでQRを読み取るだけ。ログイン不要。音声入力にも対応し、手袋をしたままでも簡単に報告できます。
                      </p>
                    </div>

                    <div className="group p-8 bg-slate-50 rounded-2xl transition hover:bg-blue-50 hover:shadow-lg border border-transparent hover:border-blue-100">
                      <div className="w-14 h-14 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition">
                        <Lock size={32} />
                      </div>
                      <h3 className="text-xl font-bold text-industrial-900 mb-3">
                        AIによる完全匿名化
                      </h3>
                      <p className="text-slate-600 leading-relaxed">
                        生成AIが個人名や特定可能な情報を自動で抽象化。「犯人探し」ではなく「原因究明」に集中できる文化を作ります。
                      </p>
                    </div>

                    <div className="group p-8 bg-slate-50 rounded-2xl transition hover:bg-emerald-50 hover:shadow-lg border border-transparent hover:border-emerald-100">
                      <div className="w-14 h-14 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-emerald-600 group-hover:scale-110 transition">
                        <BarChart3 size={32} />
                      </div>
                      <h3 className="text-xl font-bold text-industrial-900 mb-3">
                        リアルタイム可視化
                      </h3>
                      <p className="text-slate-600 leading-relaxed">
                        集まったデータは4M（人・機械・材料・方法）に自動分類。ダッシュボードで危険の兆候を即座に把握できます。
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* 使い方ガイド + 最終CTA（Claude Design 由来。旧 how-it-works / PDCA Flow / 4Mデモ誘導を一本化）。
                  4Mなぜなぜ分析デモへの送客は LandingDemoGuide 内の「4Mなぜなぜ分析を試す」が担う。 */}
              <LandingDemoGuide onTryWhyDemo={() => navigateTo('why_demo')} />
              {/* 安全掲示ポスター。使い方ガイドの STEP 1 が「印刷して現場に掲示」で終わるので、
                  その直後に「では何を貼るのか」を置くと話が途切れない。最終CTAの前に挿すことで、
                  ポスター目的で来た人もそのまま無料登録の導線に乗る。 */}
              <LandingPosters onOpenPosters={() => navigateTo('posters')} />
              <LandingClosingCTA onTryDemo={() => setView('worker_landing')} onSignup={() => navigateTo('login')} />
              {/* 運営者プロフィール (E-E-A-T 強化). 「誰が作っているのか」を顔つきで明示. */}
              <ProfileSection onClickAbout={() => navigateTo('about')} />

              {/* 更新履歴（Claude Design タイムライン版）。BtoB 検討時に「開発が止まっていないか」を可視化。 */}
              <LandingChangelog />
            </>
          )}

          {view === 'about' && (
            <AboutView onContact={() => navigateTo('legal_contact')} />
          )}
          {view === 'why_demo' && (
            <WhyAnalysisDemo
              initialProblem={whyDemoPreset?.problem || ''}
              autoStart={whyDemoPreset?.autoStart || false}
              returnLabel={whyDemoPreset?.sourceCaseId ? '← ケース一覧に戻る' : undefined}
              onReturn={whyDemoPreset?.sourceCaseId ? () => {
                // sourceCaseId があるとき: ケース一覧に戻る. preset は useEffect でクリアされる.
                navigateTo('manager_cases');
              } : undefined}
              // ケース由来 (sourceCaseId あり) のとき保存ボタンを出す.
              // sourceCaseId はログイン中のケース詳細/ダッシュボードからしかセットされないため、
              // これ自体がログイン済みの証左. 実際の保存可否は updateCaseLesson の RLS で最終判定.
              onSaveToCase={
                whyDemoPreset?.sourceCaseId
                  ? async (lessonText) => {
                      const caseId = whyDemoPreset.sourceCaseId!;
                      const target = cases.find((c) => c.id === caseId);
                      const merged = (target?.lesson || '') + lessonText;
                      const ok = await updateCaseLesson(caseId, merged);
                      if (ok) {
                        updateCase(caseId, { lesson: merged });
                        addToast({ type: 'success', message: '分析結果をケースに保存しました' });
                        navigateTo('manager_cases');
                      } else {
                        addToast({ type: 'error', message: '保存に失敗しました。再ログインの上お試しください。' });
                      }
                    }
                  : undefined
              }
              // 結果ページから安全ポスト＋への送客 (ケース由来でない=デモ利用者のみ)
              onSignupToAnzenPost={!whyDemoPreset?.sourceCaseId ? () => navigateTo('login') : undefined}
              onShowAnzenPostDeck={!whyDemoPreset?.sourceCaseId ? () => {
                window.open('/docs/anzenpostplus-overview.pdf', '_blank', 'noopener');
              } : undefined}
            />
          )}
          {view === 'guide' && <GuideView onChangeView={navigateTo} />}
          {(view === 'legal' || view === 'legal_about' || view === 'legal_privacy' || view === 'legal_transactions' || view === 'legal_contact') && (
            <LegalView initialTab={getLegalTab(view)} />
          )}
          {view === 'pricing' && <PricingView onChangeView={setView} />}
        </main>

        <Footer />
        {/* 案内チャットボット（共有バックエンド genbacompass_chat。公開ページのみ表示） */}
        <GenbaChatWidget
          appId="genbasafeai"
          endpoint="https://onndphrrqfnogqexpfgw.supabase.co/functions/v1/genbacompass_chat"
          anonKey={import.meta.env.VITE_SUPABASE_ANON_KEY}
          contactEmail="support@genbacompass.com"
          avatarUrl="/images/chat-avatar.jpg"
        />
      </div>
    );
  }

  // 1b. Industry LPs（独自ヘッダ/フッタを持つため public ページラップ外で独立レンダー）
  if (view === 'lp_construction' || view === 'lp_manufacturing' || view === 'lp_logistics') {
    const industryKey = view === 'lp_construction' ? 'construction'
      : view === 'lp_manufacturing' ? 'manufacturing'
      : 'logistics';
    return (
      <IndustryLandingView
        industryKey={industryKey}
        onTryDemo={() => setView('worker_landing')}
        onGoPricing={() => navigateTo('pricing')}
        onGoHome={() => navigateTo('landing')}
      />
    );
  }

  // 2. Worker Views (Mobile Optimized)
  if (['worker_landing', 'worker_form', 'worker_confirm', 'worker_success'].includes(view)) {
    // Check if in demo mode (no QR token means accessed from demo button)
    const isDemoMode = !currentQRToken;

    // デモモード（QRトークン無し = LPの「30秒で試す」等から）は Claude Design 由来の
    // ガイド付きデモ体験を描画。実QRトークンでの本物の報告フロー（実Gemini/Supabase送信）は
    // 従来どおり下の WorkerLayout + WorkerHome/Input/Confirm/Success を使う。
    if (isDemoMode) {
      return (
        <DemoExperience
          teamName={qrContext?.qrPointName || '東京第3工場 Bチーム'}
          onBackToLp={() => setView('landing')}
          onSignup={() => { trackSignupStarted('demo_success'); navigateTo('login'); }}
          onTryWhyDemo={() => navigateTo('why_demo')}
        />
      );
    }

    // ここから下は実QRトークンでの本物の報告フロー（isDemoMode は必ず false）。
    // レイアウト方針:
    //  - md 未満 (スマホ実機): 画面いっぱいに使う。灰色の下地も上下パディングも出さない
    //    （スマホ枠の「見せ方」はデスクトップのデモ用であって、実機では邪魔なだけ）。
    //    高さは 100dvh。iOS Safari の 100vh はアドレスバー分だけ実際より大きく、
    //    下に切れる/余る帯の原因になるため使わない。
    //  - md 以上 (デスクトップ): 従来どおり灰色下地の中央にスマホ枠を浮かせる。
    return (
      <div className="min-h-[100dvh] bg-white md:bg-slate-100 flex flex-col md:items-center md:justify-center md:py-8">
        <WorkerLayout>
          {view === 'worker_landing' && (
            <WorkerHome
              onStart={() => setView('worker_form')}
              onBack={() => setView('landing')}
              teamName={qrContext?.qrPointName || '安全報告'}
              stats={workerStats}
              organizationId={qrContext?.organizationId}
              qrToken={currentQRToken || undefined}
              onShowMyReports={() => navigateTo('my_reports_list')}
            />
          )}
          {view === 'worker_form' && (
            <WorkerInput
              qrToken={currentQRToken}
              onSubmit={handleWorkerInputSubmit}
              onCancel={() => {
                // 「戻る」= 明示的な破棄。古い入力が次回の報告に混ざらないよう
                // メモリ側（submissionData）もここで必ず捨てる。
                // sessionStorage の下書きは WorkerInput 内の handleCancel が消す。
                setSubmissionData(null);
                setView('worker_landing');
              }}
              organizationId={qrContext?.organizationId}
              equipmentInfo={qrContext?.equipmentInfo}
              initialInput={submissionData?.input}
            />
          )}
          {view === 'worker_confirm' && submissionData && (
            <WorkerConfirm
              data={submissionData}
              onConfirm={handleWorkerConfirm}
              onBack={() => setView('worker_form')}
              hasAudioInput={!!submissionData.input.audioBlob}
            />
          )}
          {view === 'worker_success' && (
            <WorkerSuccess
              onHome={() => setView('worker_landing')}
              onContinue={resetWorkerFlow}
              viewerToken={lastSubmittedCase?.viewerToken}
              reportInfo={lastSubmittedCase ? {
                caseId: lastSubmittedCase.caseId,
                title: lastSubmittedCase.title,
                body: lastSubmittedCase.body,
              } : undefined}
              stats={{
                newTotal: workerStats.monthPosts
              }}
              isDemoMode={isDemoMode}
              imageUploadFailed={lastSubmittedCase?.imageUploadFailed}
              onSignUp={() => navigateTo('login')}
              onDeepAnalyze={(problem) => {
                setWhyDemoPreset({ problem, autoStart: true });
                navigateTo('why_demo');
              }}
            />
          )}
        </WorkerLayout>
      </div>
    );
  }

  // Calculate safety score based on actual data
  // KPI Calculation Functions - New Dashboard Design
  // ⚠️ 利用上限到達 ケースは KPI のノイズになるので集計対象から除外する.
  // 別途「AI未分析」タブで一覧可能 (ManagerCasesView).
  const realCases = filterRealCases(cases);

  const getMonthRange = (monthsAgo: number) => {
    const now = new Date();
    const targetMonth = now.getMonth() - monthsAgo;
    const targetYear = now.getFullYear() + Math.floor(targetMonth / 12);
    const normalizedMonth = ((targetMonth % 12) + 12) % 12;
    return { month: normalizedMonth, year: targetYear };
  };

  const filterByMonth = (c: Case, month: number, year: number) => {
    const date = new Date(c.createdAt);
    return date.getMonth() === month && date.getFullYear() === year;
  };

  // 1. 未対応High（前月比付き）
  const calculateUnresolvedHigh = () => {
    const current = getMonthRange(0);
    const last = getMonthRange(1);

    const currentHighOpen = realCases.filter(c =>
      filterByMonth(c, current.month, current.year) &&
      c.riskLevel === RiskLevel.HIGH &&
      c.status !== 'resolved'
    ).length;

    const lastHighOpen = realCases.filter(c =>
      filterByMonth(c, last.month, last.year) &&
      c.riskLevel === RiskLevel.HIGH &&
      c.status !== 'resolved'
    ).length;

    const change = currentHighOpen - lastHighOpen;
    const changeText = change > 0 ? `+${change}` : change === 0 ? '±0' : `${change}`;
    return { count: currentHighOpen, change, changeText };
  };

  // 2. 未対応合計（前月比付き）
  const calculateUnresolvedTotal = () => {
    const current = getMonthRange(0);
    const last = getMonthRange(1);

    const currentOpen = realCases.filter(c =>
      filterByMonth(c, current.month, current.year) &&
      c.status !== 'resolved'
    ).length;

    const lastOpen = realCases.filter(c =>
      filterByMonth(c, last.month, last.year) &&
      c.status !== 'resolved'
    ).length;

    const change = currentOpen - lastOpen;
    const changeText = change > 0 ? `+${change}` : change === 0 ? '±0' : `${change}`;
    return { count: currentOpen, change, changeText };
  };

  // 3. 改善実施率（前月比付き）
  const calculateImplementationRate = () => {
    const current = getMonthRange(0);
    const last = getMonthRange(1);

    const currentCases = realCases.filter(c => filterByMonth(c, current.month, current.year));
    const currentResolved = currentCases.filter(c => c.status === 'resolved').length;
    const currentRate = currentCases.length > 0 ? Math.round((currentResolved / currentCases.length) * 100) : 0;

    const lastCases = realCases.filter(c => filterByMonth(c, last.month, last.year));
    const lastResolved = lastCases.filter(c => c.status === 'resolved').length;
    const lastRate = lastCases.length > 0 ? Math.round((lastResolved / lastCases.length) * 100) : 0;

    const change = currentRate - lastRate;
    const changeText = change > 0 ? `+${change}pt` : change === 0 ? '±0pt' : `${change}pt`;
    return { rate: currentRate, change, changeText };
  };

  // 4. 滞留日数（中央値）- 未対応案件の滞留日数
  const calculateMedianPendingDays = () => {
    const openCases = realCases.filter(c => c.status !== 'resolved');
    if (openCases.length === 0) return { days: '-', count: 0 };

    const now = new Date();
    const pendingDays = openCases.map(c => {
      const createdAt = new Date(c.createdAt);
      const diffTime = now.getTime() - createdAt.getTime();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }).sort((a, b) => a - b);

    // Calculate median
    const mid = Math.floor(pendingDays.length / 2);
    const median = pendingDays.length % 2 !== 0
      ? pendingDays[mid]
      : Math.round((pendingDays[mid - 1] + pendingDays[mid]) / 2);

    return { days: median, count: openCases.length };
  };

  // 5. 3日以上滞留している未対応案件の件数（Hero誘導用）
  const calculateStaleCount = () => {
    const now = new Date().getTime();
    const threeDaysMs = 3 * 24 * 60 * 60 * 1000;
    return realCases.filter(c => c.status !== 'resolved' && now - new Date(c.createdAt).getTime() > threeDaysMs).length;
  };

  // 3. Manager Views (Dashboard)
  return (
    <ManagerLayout
      currentView={view}
      /* 掲示ポスターは URL を持つ公開ページ（/posters）。ここだけは history を積んで
         遷移させる。他の manager_* は viewToPath が '/' に落ちるので setView のまま。 */
      onChangeView={(next) => (next === 'posters' ? navigateTo('posters') : setView(next))}
      isOperator={isOperator}
    >
      {/* 自動合流を試して断られたとき。確認バナーは出ない経路なので、
          ここで独立して理由を出す。出さないと「何も起きない」だけになる。 */}
      {!pendingInvite && inviteError && (
        <div className="mb-6 rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-bold text-industrial-900">組織に参加できませんでした</p>
          <p className="mt-1 text-sm text-slate-700">{inviteError}</p>
          <p className="mt-2 text-xs text-slate-600">
            解消されたあと、もう一度ログインすると参加できます。
            それまではこの組織をそのままお使いいただけます。
          </p>
          <button
            onClick={() => setInviteError(null)}
            className="mt-3 px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-xs font-medium rounded-lg"
          >
            閉じる
          </button>
        </div>
      )}

      {/* 別の組織から招待されているときの確認。移すものがあるときだけ出る。
          合流は取り消せないので、何がどこへ移るかを先に全部見せる。 */}
      {pendingInvite && (
        <div className="mb-6 rounded-xl border-2 border-safety-300 bg-safety-50 p-5">
          <p className="font-bold text-industrial-900">
            「{pendingInvite.organizationName}」への参加に招待されています
          </p>
          <p className="mt-2 text-sm text-slate-700">
            参加すると、いまお使いのデータ
            {[
              pendingInvite.moves.cases > 0 ? `報告 ${pendingInvite.moves.cases}件` : null,
              pendingInvite.moves.qrPoints > 0 ? `QRコード ${pendingInvite.moves.qrPoints}個` : null,
              pendingInvite.moves.teams > 0 ? `工事現場 ${pendingInvite.moves.teams}件` : null,
            ].filter(Boolean).join(' / ')}
            {' '}が「{pendingInvite.organizationName}」に移り、
            <strong>その組織の管理者から見えるようになります。</strong>
          </p>
          <ul className="mt-2 text-xs text-slate-600 space-y-1 list-disc list-inside">
            <li>すでに現場に貼ってあるQRコードは、そのまま使えます（URLは変わりません）。</li>
            <li>投稿者にお伝えした確認URLも、そのまま使えます。</li>
            <li>いまお使いの組織は空になるため削除されます。<strong>この操作は取り消せません。</strong></li>
          </ul>
          <p className="mt-2 text-xs text-slate-600">
            参加後のあなたの権限は
            <strong>{pendingInvite.role === 'admin' ? '管理者' : '担当者'}</strong>
            になります。
            {pendingInvite.role !== 'admin' && '（席数の変更とメンバーの追加はできなくなります）'}
          </p>
          {inviteError && (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {inviteError}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={acceptPendingInvite}
              disabled={inviteBusy}
              className="px-4 py-2 bg-safety-500 hover:bg-safety-600 disabled:opacity-50 text-white text-sm font-bold rounded-lg"
            >
              {inviteBusy ? '参加中...' : `データを移して参加する`}
            </button>
            <button
              onClick={() => setPendingInvite(null)}
              disabled={inviteBusy}
              className="px-4 py-2 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg"
            >
              今はしない
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            「今はしない」を選んでも招待は残ります。次回ログイン時にまたお尋ねします。
          </p>
        </div>
      )}

      {view === 'manager_dashboard' && (
        <div className="space-y-6 animate-fade-in">
          {isDataLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin h-8 w-8 text-safety-600 mr-3" />
              <span className="text-slate-600">データを読み込み中...</span>
            </div>
          ) : dataError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600">{dataError}</p>
              <button
                onClick={loadDashboardData}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                再試行
              </button>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-end">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-industrial-900">ダッシュボード</h2>
                    {organization && (
                      <span className="bg-industrial-100 text-industrial-700 px-3 py-1 rounded-full text-sm font-medium">
                        {organization.name}
                      </span>
                    )}
                  </div>
                  <p className="text-slate-500 text-sm mt-1">
                    最終更新: {new Date().toLocaleString('ja-JP')}
                  </p>
                </div>
                <button
                  onClick={loadDashboardData}
                  className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 shadow-sm transition"
                >
                  データ更新
                </button>
              </div>

              {/* 工事現場の切り替えと、現場ごとの状況。
                  組織全体の数字（下の各カード）は残す——横断で見られることが
                  この製品の価値なので、現場を選んでも消さない。 */}
              {teams.length > 0 && (() => {
                const activeTeams = teams.filter((t) => !t.archivedAt);
                const monthStart = new Date();
                monthStart.setDate(1);
                monthStart.setHours(0, 0, 0, 0);
                const thisMonth = realCases.filter((c) => new Date(c.createdAt) >= monthStart);
                /* 終了した現場でも、今月の報告があれば行に出す。
                   出さないと表の合計と組織全体のカードが食い違い、
                   「数が合わない」という問い合わせになる。 */
                const rows = [
                  ...activeTeams.map((t) => ({
                    id: t.id,
                    name: t.name,
                    cases: thisMonth.filter((c) => c.teamId === t.id),
                  })),
                  ...teams
                    .filter((t) => t.archivedAt)
                    .map((t) => ({
                      id: t.id,
                      name: `${t.name}（終了）`,
                      cases: thisMonth.filter((c) => c.teamId === t.id),
                    }))
                    .filter((r) => r.cases.length > 0),
                  { id: 'none', name: '現場の指定なし', cases: thisMonth.filter((c) => !c.teamId) },
                ].filter((r) => r.cases.length > 0 || r.id !== 'none');

                return (
                  <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                      <h3 className="font-bold text-industrial-900">工事現場ごとの今月</h3>
                      <span className="text-xs text-slate-500">
                        報告数の多い順。組織全体の数字は下のカードに出ています
                      </span>
                    </div>
                    {rows.length === 0 ? (
                      <p className="text-sm text-slate-500">今月の報告はまだありません。</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                              <th className="py-2 pr-4 font-medium">工事現場</th>
                              <th className="py-2 pr-3 text-right font-medium">報告</th>
                              <th className="py-2 pr-3 text-right font-medium">High</th>
                              <th className="py-2 text-right font-medium">未対応</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows
                              .slice()
                              .sort((a, b) => b.cases.length - a.cases.length)
                              .map((r) => (
                                <tr key={r.id} className="border-b border-slate-50 last:border-0">
                                  <td className="py-2.5 pr-4 font-medium text-industrial-900">{r.name}</td>
                                  <td className="py-2.5 pr-3 text-right tabular-nums">{r.cases.length}</td>
                                  <td className="py-2.5 pr-3 text-right tabular-nums text-red-600">
                                    {r.cases.filter((c) => c.riskLevel === RiskLevel.HIGH).length}
                                  </td>
                                  <td className="py-2.5 text-right tabular-nums">
                                    {r.cases.filter((c) => c.status === 'open').length}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* 次のアクション Hero - 「今日まず何を見ればいいか」を一目で */}
              {(() => {
                const high = calculateUnresolvedHigh().count;
                const stale = calculateStaleCount();
                const rate = calculateImplementationRate();
                const goToCases = (intent: { risk?: RiskLevel | 'all'; status?: Case['status'] | 'all' }) => {
                  setCasesNavIntent(intent);
                  setView('manager_cases');
                };
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      onClick={() => goToCases({ risk: RiskLevel.HIGH, status: 'open' })}
                      className={`text-left rounded-xl p-5 border transition active:scale-[0.99] ${
                        high > 0
                          ? 'bg-red-50 border-red-200 hover:bg-red-100 hover:border-red-300'
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-bold uppercase tracking-wide ${high > 0 ? 'text-red-700' : 'text-slate-400'}`}>
                          最優先
                        </span>
                        <AlertTriangle size={18} className={high > 0 ? 'text-red-600' : 'text-slate-400'} />
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className={`text-3xl font-extrabold ${high > 0 ? 'text-red-700' : 'text-slate-500'}`}>{high}</span>
                        <span className="text-sm text-slate-600">件</span>
                      </div>
                      <p className="text-sm text-slate-700 mt-1">未対応の High リスク</p>
                      <p className="text-xs text-slate-500 mt-2">
                        {high > 0 ? 'タップして該当ケースを確認 →' : '最優先の案件はありません'}
                      </p>
                    </button>

                    <button
                      onClick={() => goToCases({ status: 'open' })}
                      className={`text-left rounded-xl p-5 border transition active:scale-[0.99] ${
                        stale > 0
                          ? 'bg-amber-50 border-amber-200 hover:bg-amber-100 hover:border-amber-300'
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-bold uppercase tracking-wide ${stale > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
                          滞留
                        </span>
                        <Clock size={18} className={stale > 0 ? 'text-amber-600' : 'text-slate-400'} />
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className={`text-3xl font-extrabold ${stale > 0 ? 'text-amber-700' : 'text-slate-500'}`}>{stale}</span>
                        <span className="text-sm text-slate-600">件</span>
                      </div>
                      <p className="text-sm text-slate-700 mt-1">3日以上 未対応のまま</p>
                      <p className="text-xs text-slate-500 mt-2">
                        {stale > 0 ? '対応状況を見直しましょう →' : '滞留している案件はありません'}
                      </p>
                    </button>

                    <button
                      onClick={() => goToCases({ status: 'resolved' })}
                      className="text-left rounded-xl p-5 border bg-emerald-50 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition active:scale-[0.99]"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wide text-emerald-700">今月の改善</span>
                        <Percent size={18} className="text-emerald-600" />
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-extrabold text-emerald-700">{rate.rate}</span>
                        <span className="text-sm text-slate-600">%</span>
                        <span className="ml-1 text-xs text-emerald-700">前月 {rate.changeText}</span>
                      </div>
                      <p className="text-sm text-slate-700 mt-1">対応済みの割合</p>
                      <p className="text-xs text-slate-500 mt-2">完了済みの一覧を見る →</p>
                    </button>
                  </div>
                );
              })()}

              {/* 項目7: 深掘り推奨サジェスト (未対応 High > 0 のときのみ表示).
                  最優先 = 最古の未対応 High ケースを 1件選んで preset 投入 → why-demo へ. */}
              {(() => {
                const highOpen = realCases
                  .filter((c) => c.riskLevel === RiskLevel.HIGH && c.status !== 'resolved')
                  .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                if (highOpen.length === 0) return null;
                const top = highOpen[0];
                return (
                  <button
                    onClick={() => {
                      setWhyDemoPreset({
                        problem: top.body || top.originalText,
                        autoStart: true,
                        sourceCaseId: top.id,
                      });
                      navigateTo('why_demo');
                    }}
                    className="text-left w-full bg-gradient-to-r from-indigo-600 to-blue-700 hover:from-indigo-700 hover:to-blue-800 text-white rounded-xl p-5 shadow-md shadow-indigo-500/30 transition active:scale-[0.99]"
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-2xl flex-shrink-0">🔍</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold uppercase tracking-wide text-indigo-100 mb-1">
                          深掘り分析おすすめ
                        </p>
                        <p className="text-sm md:text-base font-bold leading-snug">
                          未対応 High が {highOpen.length} 件あります。最優先の「
                          <span className="underline">{top.title}</span>
                          」を 4M なぜなぜで深掘りしてみませんか?
                        </p>
                        <p className="text-xs text-indigo-100 mt-1">
                          根本原因と即時/恒久対策が AI で即時生成されます (結果はケースに保存可)
                        </p>
                      </div>
                      <span className="bg-white text-indigo-700 text-xs font-bold px-3 py-2 rounded-lg flex-shrink-0 whitespace-nowrap">
                        深掘り分析へ →
                      </span>
                    </div>
                  </button>
                );
              })()}

              {/* SA-1: 月次レポートDL CTA - 経営層・上司への報告に使える紙を作る導線 */}
              <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-industrial-900 text-white flex items-center justify-center shrink-0">
                    <Download size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-industrial-900">月次レポート（先月分）をPDFでダウンロード</p>
                    <p className="text-xs text-slate-500 mt-0.5">主要指標・リスク分布・改善実施記録 TOP5 を A4 1枚にまとめます</p>
                  </div>
                </div>
                <button
                  onClick={() => setView('manager_report')}
                  className="px-4 py-2 bg-industrial-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition flex items-center gap-2 shrink-0"
                >
                  レポートを開く
                  <ChevronRight size={16} />
                </button>
              </div>

              {/* 上段KPI - 4指標（前月比付き） */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="未対応High"
                  value={calculateUnresolvedHigh().count}
                  icon={AlertTriangle}
                  color={calculateUnresolvedHigh().count > 0 ? "bg-red-500" : "bg-slate-400"}
                  subtitle={`前月 ${calculateUnresolvedHigh().changeText}`}
                />
                <StatCard
                  title="未対応合計"
                  value={calculateUnresolvedTotal().count}
                  icon={Inbox}
                  color={calculateUnresolvedTotal().count > 0 ? "bg-orange-500" : "bg-slate-400"}
                  subtitle={`前月 ${calculateUnresolvedTotal().changeText}`}
                />
                <StatCard
                  title="改善実施率"
                  value={`${calculateImplementationRate().rate}%`}
                  icon={Percent}
                  color="bg-teal-500"
                  subtitle={`前月 ${calculateImplementationRate().changeText}`}
                />
                <StatCard
                  title="滞留日数（中央値）"
                  value={calculateMedianPendingDays().days}
                  icon={Clock}
                  color={typeof calculateMedianPendingDays().days === 'number' && calculateMedianPendingDays().days > 7 ? "bg-amber-500" : "bg-slate-400"}
                  subtitle={`未対応${calculateMedianPendingDays().count}件の中央値`}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <ActivityChart cases={realCases} />
                </div>
                <div>
                  <RiskPieChart cases={realCases} />
                </div>
              </div>

              <UsageStatsCard
                usageStats={usageStats}
                isLoading={isUsageLoading}
                planName={organization?.plan || 'free'}
              />

              {realCases.length > 0 ? (
                <RecentCasesList cases={realCases.slice(0, 5)} teams={teams} onViewAll={() => setView('manager_cases')} />
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 text-center">
                  <div className="w-14 h-14 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <QrCode className="h-7 w-7 text-safety-600" />
                  </div>
                  <h3 className="text-lg font-bold text-industrial-900 mb-1">まだ報告はありません</h3>
                  <p className="text-sm text-slate-500 mb-6">
                    QRコードを作成して現場に掲示すると、作業員のスマホから匿名の報告が届きます。
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-3">
                    <button
                      onClick={() => setView('manager_qr')}
                      className="bg-safety-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-safety-700 transition inline-flex items-center justify-center"
                    >
                      <QrCode size={18} className="mr-2" />
                      QRコードを作成する
                    </button>
                    <button
                      onClick={() => navigateTo('posters')}
                      className="border border-slate-300 text-slate-700 px-6 py-3 rounded-lg font-bold hover:bg-slate-50 transition inline-flex items-center justify-center"
                    >
                      <Printer size={18} className="mr-2" />
                      掲示ポスターを印刷する
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {view === 'manager_cases' && (
        <ManagerCasesView
          cases={cases}
          qrPoints={qrPoints}
          onUpdateCase={handleUpdateCase}
          initialRiskFilter={casesNavIntent?.risk ?? 'all'}
          initialStatusFilter={casesNavIntent?.status ?? 'all'}
          onDeepAnalyze={(body, caseId) => {
            setWhyDemoPreset({ problem: body, autoStart: true, sourceCaseId: caseId });
            navigateTo('why_demo');
          }}
          onGoToQR={() => setView('manager_qr')}
        />
      )}
      {view === 'manager_import' && (
        <ManagerImportView
          organizationId={organization?.id}
          /* 現場は渡さない。紙から取り込む報告がどの現場のものかは、
             取り込む側にしか分からない。先頭の現場を既定にすると
             無関係な現場に誤って記録される（匿名報告側と同じ問題）。
             現場の選択UIは工事現場機能で足す。それまでは現場未設定で保存する。 */
          teamId={undefined}
          onGoToCases={() => setView('manager_cases')}
          onGoToSettings={() => setView('manager_settings')}
          onCaseCreated={loadDashboardData}
        />
      )}
      {view === 'manager_qr' && (
        <ManagerQRView
          qrPoints={qrPoints}
          teams={teams}
          organization={organization}
          onCreateQR={handleCreateQR}
          onDeleteQR={handleDeleteQR}
          onToggleQR={handleToggleQR}
          onEditQR={handleEditQR}
          onOpenPosters={(qrPointId) => {
            setPosterQrPointId(qrPointId);
            navigateTo('posters');
          }}
        />
      )}
      {view === 'manager_settings' && <ManagerSettingsView />}
      {/* 運営者専用。URL を直接開かれても RPC が 42501 を返すので画面側で弾く必要はない
          （OperatorUsageView が権限なしの表示を出す）。 */}
      {view === 'operator_usage' && <OperatorUsageView />}
      {view === 'manager_report' && (
        <MonthlyReportView
          cases={realCases}
          organizationName={organization?.name}
          onBack={() => setView('manager_dashboard')}
        />
      )}
    </ManagerLayout>
  );
}

export default App;
